/**
 * General translation dictionary — the largest, catch-all i18n dictionary.
 * Extracted from hooks/useTranslation.ts so both the React hook AND the
 * non-React resolver (i18n/resolve.ts) can read it without pulling in React.
 *
 * LEAF MODULE: must not import any React hook or the translation hooks, or
 * it will reintroduce the import cycle the unified resolver was built to break.
 *
 * BUG-23 perf fix preserved: buildTranslations() runs ONCE, cached at module
 * scope; every subsequent lookup is an O(1) hash read.
 */
import { DEMO_PAYMENT_ACCELERATION_FIGURE } from '@/lib/demo/demo-figures';

let __translationsCache: Record<string, Record<string, string>> | null = null;

function buildTranslations(): Record<string, Record<string, string>> {
  const translations: Record<string, Record<string, string>> = {
    en: {
      // Charts and Data
      'chart.cashPosition': 'Cash Position',
      'chart.balanceSheet': 'Balance Sheet',
      'chart.incomeStatement': 'Income Statement',
      'chart.netCashPosition': 'Net Cash Position',
      'chart.assets': 'Assets',
      'chart.liabilities': 'Liabilities',
      'chart.revenue': 'Revenue',
      'chart.expenses': 'Expenses',
      'chart.profit': 'Profit',
      'chart.budget': 'Budget',
      'chart.actual': 'Actual',
      'chart.forecast': 'Forecast',
      'chart.variance': 'Variance',
      'chart.cashBalance': 'Cash Balance',
      'chart.moneyIn': 'Money In',
      'chart.moneyOut': 'Money Out',
      'chart.days': 'Days',
      'chart.weeks': 'Weeks',
      'chart.months': 'Months',
      'chart.quarters': 'Quarters',
      'chart.years': 'Years',
      'chart.today': 'Today',
      'chart.yAxisLabel': 'Amount',
      'chart.xAxisLabel': 'Time Period',
      'chart.noData': 'No data available',
      'chart.loading': 'Loading chart data...',
      'chart.error': 'Error loading data',

      // Agents and AI
      'agent.processing': 'Processing...',
      'agent.thinking': 'AI is thinking...',
      'agent.complete': 'Analysis complete',
      'agent.error': 'AI analysis failed',
      'agent.recommendation': 'Recommendation',
      'agent.confidence': 'Confidence',
      'agent.suggestion': 'Suggestion',

      // Navigation
      'nav.dashboard': 'Dashboard',
      'nav.messages': 'Messages',
      'nav.analytics': 'Analytics',
      'nav.settings': 'Settings',
      'nav.receivables': 'Receivables',
      'nav.payables': 'Payables',
      'nav.contracts': 'Contracts',
      'nav.keyChanges': 'Key Changes',
      'nav.whatsDrivingIt': "What's Driving It",
      'nav.takeAction': 'Take Action',
      'nav.allMessages': 'All Messages',
      'nav.priorities': 'Priorities',
      'nav.prioritySettings': 'Priority Settings',
      'nav.priorityAnalysis': 'Analysis',
      'nav.categories': 'Categories',
      'nav.allContracts': 'All Contracts',
      'nav.upload': 'Upload',
      'nav.analysis': 'Analysis',
      'nav.invoices': 'Invoices',
      'nav.credits': 'Credits',
      'nav.reports': 'Reports',
      'nav.dataExport': 'Data Export',
      'nav.suppliers': 'Suppliers',
      'nav.approvals': 'Approvals',
      'nav.paymentsOut': 'Payments Out',
      'nav.paymentsIn': 'Payments In',
      'nav.reconciliation': 'Reconciliation',
      'nav.trends': 'Trends',
      'nav.batchPayments': 'Batch Payments',
      'nav.vendorManagement': 'Vendor Management',
      'nav.general': 'General',
      'nav.users': 'Users',
      'nav.integrations': 'Integrations',
      'nav.overview': 'Overview',
      'nav.details': 'Details',
      'nav.actions': 'Actions',
      'nav.buyers': 'Buyers',
      'nav.aiAssistant': 'AI Assistant',
      'nav.aiAssistantDesc': 'Chat with AI agents for financial assistance',

      // Dashboard
      'dashboard.welcomeBack': 'Welcome back',
      'dashboard.availableCredit': 'Available Credit',
      'dashboard.creditUtilized': 'Credit Utilized',
      'dashboard.creditLine': 'Credit Line',
      'dashboard.scheduled': 'Scheduled',
      'dashboard.paid': 'Paid',
      'dashboard.dataSources': 'Data Sources',
      'dashboard.activeAgents': 'Active Agents',
      'dashboard.docsProcessed': 'docs processed',
      'dashboard.agentMonitorMessage': 'Agents continuously monitor your financial documents',
      'dashboard.goDeeperToControllers': 'Go Deeper to Controllers',

      // Personas
      'persona.financialController': 'Financial Controller',
      'persona.analyst': 'Analyst',
      'persona.ceo': 'CEO',
      'persona.cfo': 'CFO',

      // Dashboard sections
      'sections.keyChanges': 'Key Changes',
      'sections.whatsDrivingIt': "What's Driving It",
      'sections.takeAction': 'Take Action',
      'sections.keyChangesSubtitle': 'Important updates and metrics that need your attention',
      'sections.whatsDrivingItSubtitle': 'Analysis and insights behind the numbers',
      'sections.takeActionSubtitle': 'Recommendations and next steps to improve performance',

      // Payment opportunities
      'payments.saveMoneyEarly': 'Save $3,400 with Early Payment',
      'payments.earlyPaymentDesc': 'Take 2% discount on supplier invoices',
      'payments.opportunitiesTitle': 'AI-detected opportunities to save money with early payments',
      'payments.youSaveMoney': 'You Save Money',
      'payments.saveMoneyDesc':
        'Accept the offer to reduce your payment amount and improve cash flow',

      // Persona messages
      'persona.controller.welcome': `Welcome! I found ${DEMO_PAYMENT_ACCELERATION_FIGURE} in payment acceleration opportunities`,
      'persona.controller.insight': 'Your DSO is 15 days higher than industry average',
      'persona.controller.action': 'Review payment acceleration recommendations',
      'persona.analyst.welcome': "Welcome! I've identified cross-border optimization opportunities",
      'persona.analyst.insight': 'FX exposure analysis shows $2,340 in potential savings',
      'persona.analyst.action': 'Analyze international payment patterns',
      'persona.ceo.welcome': "Welcome! I've identified contract qualification improvements",
      'persona.ceo.insight': 'Financial strength indicators show readiness for larger contracts',
      'persona.ceo.action': 'Review strategic growth opportunities',
      'persona.cfo.welcome': "Welcome! I've prepared your executive financial overview",
      'persona.cfo.insight': 'Cash flow projections show 23% improvement potential',
      'persona.cfo.action': 'Review strategic financial recommendations',

      // Business outcomes
      'business.getPaidFaster': 'Get Paid Up to 60 Days Faster',
      'business.growCrossBorder': 'Grow Cross-Border Revenue',
      'business.qualifyContracts': 'Qualify for Larger Contracts',
      'business.getPaidFasterDesc': 'AI-powered payment acceleration for manufacturing controllers',
      'business.growCrossBorderDesc': 'International expansion opportunities for analysts',
      'business.qualifyContractsDesc': 'Strategic growth positioning for leadership',
      'business.opportunity': 'Business Opportunity',
      'business.potentialValue': 'potential value',

      // Controllers
      'controllers.tellTale': 'Tell Tale - Email Parser',
      'controllers.fullSend': 'Full Send - QTC Orchestrator',
      'controllers.poniente': 'Poniente - Cash Flow Stabilizer',
      'controllers.levante': 'Levante - Credit Risk Manager',
      'controllers.configure': 'Configure Controllers',

      // Contracts
      'contracts.upload': 'Upload',
      'contracts.analysis': 'Analysis',
      'contracts.creditScoring': 'Credit Scoring',
      'contracts.dataManagement': 'Data Management',
      'contracts.earlyPayment': 'Early Payment',
      'contracts.language': 'Language',
      'pages.contracts': 'Contracts',

      // Common
      'common.loading': 'Loading...',
      'common.error': 'Error',
      'common.success': 'Success',
      'common.cancel': 'Cancel',
      'common.save': 'Save',
      'common.delete': 'Delete',
      'common.edit': 'Edit',
      'common.view': 'View',
      'common.search': 'Search...',
      'common.filter': 'Filter',
      'common.sort': 'Sort',
      'common.export': 'Export',
      'common.import': 'Import',
      'common.all': 'All',
      'common.retry': 'Retry',
      'common.add': 'Add',

      // Status
      'status.active': 'Active',
      'status.inactive': 'Inactive',
      'status.pending': 'Pending',
      'status.completed': 'Completed',
      'status.failed': 'Failed',

      // Messages
      'message.priority.high': 'High Priority',
      'message.priority.medium': 'Medium Priority',
      'message.priority.low': 'Low Priority',
      'message.category.invoice': 'Invoice',
      'message.category.payment': 'Payment',
      'message.category.general': 'General',

      // Quick Actions
      'action.processPayment': 'Process Payment',
      'action.createPO': 'Create PO',
      'action.markComplete': 'Mark Complete',
      'action.aiActions': 'AI Actions',
      'action.aiAnalyze': 'AI Analyze',
      'action.scheduleFollowup': 'Schedule Follow-up',
      'action.aiEnhanced': 'AI Enhanced',
      'action.aiInsights': 'AI Insights',
      'action.fullMessage': 'Full Message',
      'action.highUrgency': 'High Urgency',

      // Buyers
      'buyers.subtitle': 'Manage your customer relationships and credit profiles',
      'buyers.addBuyer': 'Add Buyer',
      'buyers.title': 'Buyers',

      // Chat
      'chat.welcomeMessage':
        '👋 Hi! I can help with emails, financial operations, payments, and support. What would you like to know?',
      'chat.placeholder': 'Ask anything...',
      'chat.aiAssistant': 'AI Assistant',
      'chat.smart': 'Smart',
      'chat.askAnything': 'Ask anything about your business',
      'chat.thinking': 'Thinking...',
      'chat.errorMessage': 'Sorry, I encountered an error. Please try again.',
      'chat.dataProcessed': 'Data processed by AI agents',
      // Cash-flow widget in the assistant panel (BUG-87 AC2)
      'chat.cashFlowWidget.position': 'Cash position',
      'chat.cashFlowWidget.net': 'Net position',
      'chat.cashFlowWidget.receivables': 'Receivables',
      'chat.cashFlowWidget.payables': 'Payables',
      'chat.cashFlowWidget.overdue': 'Overdue',
      'chat.cashFlowWidget.viewDetail': 'View cash flow detail',
      'chat.quickQuery1': "What's my cash position?",
      'chat.quickQuery2': 'Process my emails',
      'chat.quickQuery3': 'Help me with payments',
      'chat.quickQuery4': 'Show overdue invoices',

      // Priorities
      'priorities.cashFlowManagement': 'Cash Flow Management',
      'priorities.paymentAcceleration': 'Payment Acceleration',

      // Insights
      'insights.cashFlowOptimization': 'Cash Flow Optimization',
      'insights.paymentTermsAnalysis': 'Payment Terms Analysis',

      // Recommendations
      'recommendations.automateInvoiceProcessing': 'Automate Invoice Processing',
      'recommendations.optimizePaymentTerms': 'Optimize Payment Terms',

      // Receivables Table
      'receivables.table.invoiceNumber': 'Invoice #',
      'receivables.table.customer': 'Customer',
      'receivables.table.amount': 'Amount',
      'receivables.table.dueDate': 'Due Date',
      'receivables.table.alisioForecast': 'Alisio Forecast',
      'receivables.table.predictedPay': 'Predicted Pay',
      'receivables.table.status': 'Status',
      'receivables.table.riskScore': 'Risk Score',
      'receivables.table.daysOverdue': 'Days Overdue',
      'receivables.table.actions': 'Actions',

      // KPI Card
      'kpi.financialIntelligence': 'Financial Intelligence',
      'kpi.liveDataAnalysis': 'Live Data Analysis',
      'kpi.loading': 'Loading KPIs...',
      'kpi.error': 'Error loading KPIs',
      'kpi.metricsTracked': 'key metrics tracked',
      'kpi.realtimeIntelligence': 'Real-time financial intelligence',
      'kpi.allSystemsActive': 'All systems active',
      'kpi.priority': 'priority',

      // Budget vs Actuals
      'bva.tooltip': 'Compare your planned budget with actual performance',
      'bva.line': 'Line',
      'bva.bar': 'Bar',
      'bva.weekly': 'Weekly',
      'bva.monthly': 'Monthly',
      'bva.quarterly': 'Quarterly',
      'bva.yearly': 'Yearly',
      'bva.budgetVsActual': 'Budget vs Actual',
      'bva.thisMonthBudget': 'This Month Budget',
      'bva.thisMonthActual': 'This Month Actual',
      'bva.variance': 'Variance',
      'bva.averageVariance': '6-Month Average Variance:',
      'bva.performingAbove': 'Performing above budget',
      'bva.performingBelow': 'Performing below budget',

      // Dashboard Sections
      'dashboard.recommended': 'Recommended',
      'dashboard.addCharts': 'Add Charts',
      'dashboard.agentRecommendations': 'Agent Recommendations',
      'dashboard.allCharts': 'All Charts',

      // KPI Metrics
      'metrics.keyMetrics': 'Key Metrics',
      'metrics.essentialIndicators': 'Essential performance indicators',
      'metrics.cashPosition': 'Cash Position',
      'metrics.paymentEfficiency': 'Payment Efficiency',
      'metrics.compliance': 'Compliance',
      'metrics.improving': 'Improving',
      'metrics.onTarget': 'On Target',
      'metrics.excellent': 'Excellent',
      'metrics.attention': 'Attention',
      'metrics.neutral': 'Neutral',
      'metrics.onTrack': 'On track',
      'metrics.current': 'Current',
      'metrics.summary': 'Summary:',
      'metrics.allPerforming':
        'All key metrics are performing well. Cash position improving, payments on track, compliance current.',

      // Recommendations
      'recommendations.smartRecommendations': 'Smart Recommendations',
      'recommendations.actionsToOptimize': 'Actions to optimize your financial position',
      'recommendations.updated': 'Updated',
      'recommendations.roleFocus': 'Priority for your role',
      'recommendations.period30d': '30 days',
      'recommendations.period90d': '90 days',
      'recommendations.period12m': '12 months',
      'recommendations.quickWin': 'QUICK WIN',
      'recommendations.strategic': 'STRATEGIC',
      'recommendations.automated': 'AUTOMATED',
      'recommendations.impact': 'Impact:',
      'recommendations.available': 'recommendations available.',
      'recommendations.quickWinsSave': 'Quick wins can save',
      'recommendations.strategicFreeUp': 'Strategic actions can free up',
      'recommendations.immediately': 'immediately.',

      // Recommendation 1
      'rec1.title': 'Take Early Payment Discounts',
      'rec1.description': 'Pay suppliers early to capture 2% discounts on $280K in invoices',
      'rec1.impact': 'Save $5,600 immediately',
      'rec1.timeEstimate': '5 minutes',
      'rec1.action': 'Review & Pay',

      // Recommendation 2
      'rec2.title': 'Delay Equipment Purchase',
      'rec2.description': 'Postpone $340K equipment purchase by 30 days to improve cash flow',
      'rec2.impact': 'Free up $340K for working capital',
      'rec2.timeEstimate': '15 minutes',
      'rec2.action': 'Reschedule',

      // Recommendation 3
      'rec3.title': 'Set Up Payment Reminders',
      'rec3.description': 'Automate payment reminders for customers with overdue invoices',
      'rec3.impact': 'Reduce DSO by 5-7 days',
      'rec3.timeEstimate': '2 minutes',
      'rec3.action': 'Enable',

      // Recommendation 4
      'rec4.title': 'Review Credit Terms',
      'rec4.description': 'Negotiate better payment terms with suppliers based on payment history',
      'rec4.impact': 'Extend payment terms by 15 days',
      'rec4.timeEstimate': '10 minutes',
      'rec4.action': 'Contact Suppliers',

      // Payables
      'payables.controller': 'Controller',
      'payables.financeOps': 'Finance Ops',
      'payables.learningJourney': 'Learning Journey',
      'payables.createPayment': 'Create Payment',
      'payables.totalBills': 'Total Bills',
      'payables.pending': 'Pending',
      'payables.overdue': 'Overdue',
      'payables.potentialSavings': 'Potential Savings',
      'payables.earlyPaymentDiscounts': 'Early payment discounts',
      'payables.payablesFinancing': 'Payables Financing',
      'payables.optimizeWorkingCapital':
        'Optimize your working capital with flexible payment solutions',
      'payables.newFeature': 'New Feature',
      'payables.extendedPaymentTerms': 'Extended Payment Terms',
      'payables.availableCredit': 'Available Credit',
      'payables.applyForFinancing': 'Apply for Financing',
      'payables.viewOptions': 'View Options',
      'payables.billsOverdue': 'bills overdue',
      'payables.billOverdue': 'bill overdue',
      'payables.totalOverdueAmount': 'Total overdue amount',
      'payables.review': 'Review',
      'payables.billsAndPayments': 'Bills & Payments',
      'payables.managePayables': 'Manage your payables with intelligent insights and automation',
      'payables.table.billNumber': 'Bill Number',
      'payables.table.supplier': 'Supplier',
      'payables.table.amount': 'Amount',
      'payables.table.dueDate': 'Due Date',
      'payables.table.status': 'Status',
      'payables.table.predictedPay': 'Predicted Pay',
      'payables.table.savings': 'Savings',
      'payables.table.terms': 'Terms',
      'payables.table.automation': 'Automation',
      'payables.table.actions': 'Actions',
      'payables.table.search': 'Search bills...',
      'payables.table.noBills': 'No bills found matching your search criteria.',
      'payables.table.daysOverdue': 'Days Overdue',
      'payables.status.pending': 'Pending',
      'payables.status.overdue': 'Overdue',
      'payables.status.paid': 'Paid',
      'payables.status.scheduled': 'Scheduled',
      'payables.status.draft': 'Draft',
      'payables.status.approved': 'Approved',
      'payables.noBills': 'No bills found',
      'payables.noBillsDescription':
        'Bills will appear here once they are created or imported from your connected accounting system.',
      'payables.createFirstBill': 'Create a Bill',
      'payables.managePayablesWithInsights':
        'Manage your payables with intelligent insights and automation',
      'payables.paymentTermsDays': '30-90 days',
      'payables.discountRange': '2-5%',
      'payables.availableCreditAmount': '$50K+',
      'payables.earlyPaymentDiscountsLabel': 'Early Payment Discounts',
      'payables.availableCreditLabel': 'Available Credit',
      'payables.dpo': 'DPO (Days Payable Outstanding)',

      // Payments In
      'paymentsIn.overview': 'Overview',
      'paymentsIn.recentPayments': 'Recent Payments',
      'paymentsIn.virtualAccounts': 'Virtual Accounts',
      'paymentsIn.reconciliation': 'Reconciliation',
      'paymentsIn.totalReceived': 'Total Received',
      'paymentsIn.pendingPayments': 'Pending Payments',
      'paymentsIn.activeCustomers': 'Active Customers',
      'paymentsIn.multiCurrency': 'Multi-Currency',
      'paymentsIn.activeVirtualAccounts': 'Active virtual accounts',
      'paymentsIn.virtualAccountsSummary': 'Virtual Accounts Summary',
      'paymentsIn.usdAccount': 'USD Account',
      'paymentsIn.eurAccount': 'EUR Account',
      'paymentsIn.gbpAccount': 'GBP Account',
      'paymentsIn.paymentReconciliation': 'Payment Reconciliation',
      'paymentsIn.unreconciledPayments': 'Unreconciled Payments',
      'paymentsIn.paymentsNeedMatching': 'payments need matching',
      'paymentsIn.reconcile': 'Reconcile',
      'paymentsIn.fromLastMonth': 'from last month',
      'paymentsIn.paymentsPending': 'payments pending',
      'paymentsIn.received': 'Received',
      'paymentsIn.ago': 'ago',
      'paymentsIn.hoursAgo': 'hours ago',
      'paymentsIn.dayAgo': 'day ago',
      'paymentsIn.daysAgo': 'days ago',

      // Component Titles
      'component.keyMetrics': 'Key Metrics',
      'component.dataSources': 'Data Sources',
      'component.prioritySupport': 'Priority Support',
      'component.cashPosition': 'Cash Position',
      'component.waterfallCashFlow': 'Waterfall Cash Flow',
      'component.laborCosts': 'Labor Costs (Line Type)',
      'component.budgetVsActuals': 'Budget vs Actuals',
      'component.scenarioPlanning': 'Scenario Planning',
      'component.availableCredit': 'Available Credit',
      'component.whatIfSimulation': 'What-If Simulation',
      'component.alertsNotifications': 'Alerts & Notifications',
      'component.smartRecommendations': 'Smart Recommendations',
      'component.keyInsights': 'Key Insights',
      'component.approvalManagement': 'Approval Management',
      'component.approvalSummary': 'Approval Summary',
      'component.billsOverview': 'Bills Overview',
      'component.latePaymentTracker': 'Late Payment Tracker',
      'component.roiCalculator': 'ROI Calculator',
      'component.paymentAnalysisGrid': 'Payment Analysis Grid',
      'component.crossBorderAnalysis': 'Cross-Border Analysis',
      'component.complianceMonitoring': 'Compliance Monitoring',
      'component.cashForecastPlanning': 'Cash Forecast Planning',
      'component.vendorPaymentOptimization': 'Vendor Payment Optimization',
      'component.dsoGauge': 'DSO Gauge',
      'component.cashTrend': 'Cash Trend',
      'component.kpiTrend': 'KPI Trend',
      'component.cashForecast': 'Cash Forecast',
      'component.cashWaterfall': 'Cash Waterfall',
      'component.fxExposure': 'FX Exposure',

      // Dashboard Cards
      'dashboard.cashPosition': 'Cash Position',
      'dashboard.dueThisWeek': 'Due This Week',
      'dashboard.collectible': 'Collectible',
      'dashboard.cashPinchExpected': 'Cash pinch expected Day',
      'dashboard.receivablesHealth': 'Receivables Health',
      'dashboard.daysDSO': 'days DSO',
      'dashboard.totalAR': 'Total AR',
      'dashboard.atRisk': 'At Risk (61+ days)',
      'dashboard.opportunities': 'Opportunities',
      'dashboard.found': 'found',
      'dashboard.utilization': 'Utilization',
      'dashboard.used': 'Used',
      'dashboard.creditLineLabel': 'Line',

      // Dashboard Header
      'dashboard.financialOverview': 'Your financial overview and insights',
      'dashboard.match': 'match',
      'dashboard.components': 'Components',
      'dashboard.configure': 'Configure',
      'dashboard.insights': 'Insights',

      // Context Insights
      'dashboard.systemPerformance': 'System Performance for',
      'dashboard.efficiency': 'Efficiency',
      'dashboard.automation': 'Automation',
      'dashboard.intelligenceProfile': 'Intelligence Profile Insights',
      'dashboard.smartRecommendations': 'Smart Recommendations',
      'dashboard.impact': 'impact',
      'dashboard.activeComponentsTitle': 'Active Components',
      'dashboard.componentsActive': 'components currently active',
      'dashboard.controllerProfile': 'Controller Profile',
      'dashboard.optimizedLayout': 'Optimized dashboard layout for your role',
      'dashboard.geographicComplexity': 'Geographic Complexity',
      'dashboard.multiCurrencyEnabled': 'Multi-currency features enabled',
      'dashboard.automationOpportunity': 'Automation Opportunity',
      'dashboard.workflowAutomation': 'Potential for workflow automation',
      'dashboard.excelDependency': 'Excel Dependency',
      'dashboard.excelGridPrioritized': 'Excel-like grid components prioritized',
      'dashboard.enableDualMode': 'Enable Dual-Mode Charts',
      'dashboard.dualModeDesc': 'Switch between chart and Excel views for deeper analysis',
      'dashboard.enableNow': 'Enable Now',
      'dashboard.activateFormulas': 'Activate Agent Formulas',
      'dashboard.formulasDesc': 'Use AI-powered formulas in spreadsheet components',
      'dashboard.activate': 'Activate',
      'dashboard.crossBorderOpt': 'Cross-Border Optimization',
      'dashboard.crossBorderDesc': 'Optimize multi-currency operations with Windrose',
      'dashboard.complianceMonitoring': 'Automated Compliance Monitoring',
      'dashboard.complianceDesc': 'Set up HookedIn for regulatory compliance tracking',
      'dashboard.setup': 'Setup',
      'dashboard.productivity': 'Productivity',
      'dashboard.intelligence': 'Intelligence',
      'dashboard.operations': 'Operations',
      'dashboard.complianceCategory': 'Compliance',

      // Alert Accordion
      'dashboard.alert.dsoIncreased': 'DSO Increased by 11 Days',
      'dashboard.alert.dsoDesc': 'Impacts liquidity runway by -9 days',
      'dashboard.alert.dsoRec': 'Run cash projection analysis',
      'dashboard.alert.buyerCreditLimit': 'Buyer Z Near Credit Limit',
      'dashboard.alert.buyerCreditDesc': '92% of insured credit line used',
      'dashboard.alert.buyerCreditRec': 'Request Allianz limit review',
      'dashboard.alert.paymentAnomaly': 'Payment Pattern Anomaly',
      'dashboard.alert.paymentAnomalyDesc': 'Customer XYZ showing irregular payment behavior',
      'dashboard.alert.paymentAnomalyRec': 'Review credit terms',
      'dashboard.alert.docVerification': 'Document Verification Required',
      'dashboard.alert.docVerificationDesc': 'Multiple invoices pending verification',
      'dashboard.alert.docVerificationRec': 'Review document requirements',
      'dashboard.alert.earlyPaymentOpp': 'Early Payment Opportunity',
      'dashboard.alert.earlyPaymentDesc': 'Potential 2% discount available',
      'dashboard.alert.earlyPaymentRec': 'Consider early payment',
      'dashboard.alert.creditRatingUpdated': 'Credit Rating Updated',
      'dashboard.alert.creditRatingDesc': 'Customer ABC credit score improved',
      'dashboard.alert.creditRatingRec': 'Review credit limits',
      'dashboard.alert.cashFlow': 'Cash Flow',
      'dashboard.alert.creditRisk': 'Credit Risk',
      'dashboard.alert.riskManagement': 'Risk Management',
      'dashboard.alert.optimization': 'Optimization',
      'dashboard.alert.information': 'Information',
      'dashboard.alert.agent': 'Agent',
      'dashboard.alert.priority': 'Priority',
      'dashboard.alert.status': 'Status',
      'dashboard.alert.recommendation': 'Recommendation',

      // Spreadsheet Overlay
      'dashboard.accessRestricted': 'Access Restricted',
      'dashboard.noPermission':
        "You don't have permission to access Windrose Metrics refinement. Please contact your administrator.",
      'dashboard.windroseSaved': 'Windrose Metrics Updated',
      'dashboard.saveSuccess': 'Agent metric refinements have been saved successfully.',
      'dashboard.saveFailed': 'Failed to save metric refinements. Please try again.',
      'dashboard.simulationMode': 'Windrose Simulation Mode',
      'dashboard.simulationActive':
        'Simulation mode active - Windrose outputs will use refined values',
      'dashboard.activeRefinements': 'Active Refinements',
      'dashboard.refinementSettings': 'Refinement Settings',
      'dashboard.metricRefinements': 'Windrose Metric Refinements',
      'dashboard.reset': 'Reset',
      'dashboard.saving': 'Saving...',
      'dashboard.save': 'Save',
      'dashboard.permissionScope': 'Permission Scope',
      'dashboard.autoBackup': 'Auto-backup Frequency',
      'dashboard.everyHour': 'Every Hour',
      'dashboard.daily': 'Daily',
      'dashboard.weekly': 'Weekly',
      'dashboard.tableAgent': 'Agent',
      'dashboard.tableMetric': 'Metric',
      'dashboard.tableOriginal': 'Original',
      'dashboard.tableRefined': 'Refined Value',
      'dashboard.tableEnabled': 'Enabled',

      // Forex Dashboard
      'forex.title': 'FX & Virtual Accounts',
      'forex.subtitle': 'Manage virtual accounts, FX rates, and currency conversions',
      'forex.lastUpdated': 'Last updated:',
      'forex.refresh': 'Refresh',
      'forex.totalBalance': 'Total Balance',
      'forex.fxExposure': 'FX Exposure',
      'forex.dailyPL': 'Daily P&L',
      'forex.activeHedges': 'Active Hedges',
      'forex.virtualAccounts': 'Virtual Accounts',
      'forex.fxRates': 'FX Rates',
      'forex.transactions': 'Transactions',
      'forex.hedging': 'Hedging',

      // Onboarding
      'onboarding.welcomeTitle': 'Welcome to Alisio',
      'onboarding.welcomeSubtitle': 'Your AI-powered financial operations platform',
      'onboarding.yourProfile': 'Your Profile',
      'onboarding.role': 'Role',
      'onboarding.companySize': 'Company Size',
      'onboarding.industry': 'Industry',
      'onboarding.company': 'Company',
      'onboarding.whatsNext': "What's Next?",
      'onboarding.whatsNextDesc':
        "We'll help you configure your AI priorities based on your role and business needs.",
      'onboarding.step1Title': 'Set Your Priorities',
      'onboarding.step1Desc': 'Choose which areas AI should focus on most',
      'onboarding.step2Title': 'Meet Your AI Team',
      'onboarding.step2Desc': 'Get introduced to your personalized AI agents',
      'onboarding.step3Title': 'Start Using Alisio',
      'onboarding.step3Desc': 'Begin optimizing your financial operations',
      'onboarding.setupProgress': 'Setup Progress',
      'onboarding.stepOf': 'Step {current} of {total}',
      'onboarding.back': 'Back',
      'onboarding.continue': 'Continue',
      'onboarding.skip': 'Skip',
      'onboarding.getStarted': 'Get Started with Alisio',
      'onboarding.setupComplete': 'Setup Complete!',
      'onboarding.setupCompleteDesc':
        'Your AI team is ready to help optimize your financial operations',
      'onboarding.yourConfig': 'Your Configuration',
      'onboarding.prioritySettings': 'Priority Settings',
      'onboarding.whatYouCanDo': 'What You Can Do Now',
      'onboarding.viewMessages': 'View Priority-Based Messages',
      'onboarding.viewMessagesDesc': 'See AI insights organized by your priorities',
      'onboarding.adjustSettings': 'Adjust Settings',
      'onboarding.adjustSettingsDesc': 'Modify priorities anytime in Settings',
      'onboarding.connectSystems': 'Connect Your Systems',
      'onboarding.connectSystemsDesc': 'Integrate with your existing financial tools',

      // Tell Tale email widget (BUG-114) — ported from the inline dict during
      // the useTranslation extraction/merge so no key is lost.
      'telltale.recentEmails': 'Recent Emails',
      'telltale.total': 'total',
      'telltale.unread': 'unread',
      'telltale.noEmails': 'No emails to display',
      'telltale.refreshInbox': 'Refresh Inbox',
      'telltale.refresh': 'Refresh',
      'telltale.processAll': 'Process All',
      'telltale.viewFullInbox': 'View Full Inbox',
      'telltale.view': 'View',
      'telltale.process': 'Process',
      'telltale.cashImpact': 'Cash Impact',
      'telltale.extracted': 'Extracted',
      'telltale.amount': 'Amount',
      'telltale.due': 'Due',
      'telltale.actionRequired': 'Action Required',
      'telltale.recently': 'Recently',
      'telltale.justNow': 'Just now',
      'telltale.hoursAgo': 'h ago',
      'telltale.daysAgo': 'd ago',
      'telltale.insight': 'Tell Tale Insight',
      'telltale.processedPrefix': "I've processed",
      'telltale.processedSuffix': 'emails automatically.',
      'telltale.needAttention': 'emails need your attention.',
      'telltale.foundFinancialData': 'Found financial data that may impact cash flow.',

      // Payables action labels + unavailable notice (BUG-109) — ported during merge.
      'payables.actions.schedulePayment': 'Schedule Payment',
      'payables.actions.schedule': 'Schedule',
      'payables.actions.earlyPayment': 'Early Payment',
      'payables.actions.savingsSuffix': 'savings',
      'payables.actions.negotiateTerms': 'Negotiate Terms',
      'payables.actions.unavailableBadge': 'Unavailable',
      'payables.actions.unavailableTitle': 'Action not available yet',
      'payables.actions.unavailableDescription':
        'This action is not available yet. Nothing has been scheduled, paid or sent.',
    },
    es: {
      // Charts and Data
      'chart.cashPosition': 'Posición de Efectivo',
      'chart.balanceSheet': 'Balance General',
      'chart.incomeStatement': 'Estado de Resultados',
      'chart.netCashPosition': 'Posición Neta de Efectivo',
      'chart.assets': 'Activos',
      'chart.liabilities': 'Pasivos',
      'chart.revenue': 'Ingresos',
      'chart.expenses': 'Gastos',
      'chart.profit': 'Ganancia',
      'chart.budget': 'Presupuesto',
      'chart.actual': 'Real',
      'chart.forecast': 'Pronóstico',
      'chart.variance': 'Variación',
      'chart.cashBalance': 'Balance de Efectivo',
      'chart.moneyIn': 'Dinero Entrante',
      'chart.moneyOut': 'Dinero Saliente',
      'chart.days': 'Días',
      'chart.weeks': 'Semanas',
      'chart.months': 'Meses',
      'chart.quarters': 'Trimestres',
      'chart.years': 'Años',
      'chart.today': 'Hoy',
      'chart.yAxisLabel': 'Cantidad',
      'chart.xAxisLabel': 'Período de Tiempo',
      'chart.noData': 'No hay datos disponibles',
      'chart.loading': 'Cargando datos del gráfico...',
      'chart.error': 'Error al cargar datos',

      // Agents and AI
      'agent.processing': 'Procesando...',
      'agent.thinking': 'IA está pensando...',
      'agent.complete': 'Análisis completo',
      'agent.error': 'Error en análisis de IA',
      'agent.recommendation': 'Recomendación',
      'agent.confidence': 'Confianza',
      'agent.suggestion': 'Sugerencia',

      // Navigation
      'nav.dashboard': 'Panel de Control',
      'nav.messages': 'Mensajes',
      'nav.analytics': 'Analíticas',
      'nav.settings': 'Configuración',
      'nav.receivables': 'Cuentas por Cobrar',
      'nav.payables': 'Cuentas por Pagar',
      'nav.contracts': 'Contratos',
      'nav.keyChanges': 'Cambios Clave',
      'nav.whatsDrivingIt': 'Qué lo Impulsa',
      'nav.takeAction': 'Tomar Acción',
      'nav.allMessages': 'Todos los Mensajes',
      'nav.priorities': 'Prioridades',
      'nav.prioritySettings': 'Configuración de Prioridad',
      'nav.priorityAnalysis': 'Análisis',
      'nav.categories': 'Categorías',
      'nav.allContracts': 'Todos los Contratos',
      'nav.upload': 'Subir',
      'nav.analysis': 'Análisis',
      'nav.invoices': 'Facturas',
      'nav.credits': 'Créditos',
      'nav.reports': 'Reportes',
      'nav.dataExport': 'Exportar Datos',
      'nav.suppliers': 'Proveedores',
      'nav.approvals': 'Aprobaciones',
      'nav.paymentsOut': 'Pagos Salientes',
      'nav.paymentsIn': 'Pagos Entrantes',
      'nav.reconciliation': 'Reconciliación',
      'nav.trends': 'Tendencias',
      'nav.batchPayments': 'Pagos por Lote',
      'nav.vendorManagement': 'Gestión de Proveedores',
      'nav.general': 'General',
      'nav.users': 'Usuarios',
      'nav.integrations': 'Integraciones',
      'nav.overview': 'Resumen',
      'nav.details': 'Detalles',
      'nav.actions': 'Acciones',
      'nav.buyers': 'Compradores',
      'nav.aiAssistant': 'Asistente IA',
      'nav.aiAssistantDesc': 'Chatea con agentes IA para asistencia financiera',

      // Dashboard
      'dashboard.welcomeBack': 'Bienvenido de nuevo',
      'dashboard.availableCredit': 'Crédito Disponible',
      'dashboard.creditUtilized': 'Crédito Utilizado',
      'dashboard.creditLine': 'Línea de Crédito',
      'dashboard.scheduled': 'Programado',
      'dashboard.paid': 'Pagado',
      'dashboard.dataSources': 'Fuentes de Datos',
      'dashboard.activeAgents': 'Agentes Activos',
      'dashboard.docsProcessed': 'docs procesados',
      'dashboard.agentMonitorMessage':
        'Los agentes monitorean continuamente sus documentos financieros',
      'dashboard.goDeeperToControllers': 'Profundizar en Controladores',

      // Personas
      'persona.financialController': 'Controlador Financiero',
      'persona.analyst': 'Analista',
      'persona.ceo': 'CEO',
      'persona.cfo': 'CFO',

      // Dashboard sections
      'sections.keyChanges': 'Cambios Clave',
      'sections.whatsDrivingIt': 'Qué lo Impulsa',
      'sections.takeAction': 'Tomar Acción',
      'sections.keyChangesSubtitle':
        'Actualizaciones importantes y métricas que requieren su atención',
      'sections.whatsDrivingItSubtitle': 'Análisis e insights detrás de los números',
      'sections.takeActionSubtitle': 'Recomendaciones y próximos pasos para mejorar el rendimiento',

      // Payment opportunities
      'payments.saveMoneyEarly': 'Ahorre $3,400 con Pago Anticipado',
      'payments.earlyPaymentDesc': 'Tome 2% de descuento en facturas de proveedores',
      'payments.opportunitiesTitle':
        'Oportunidades detectadas por IA para ahorrar dinero con pagos anticipados',
      'payments.youSaveMoney': 'Usted Ahorra Dinero',
      'payments.saveMoneyDesc':
        'Acepte la oferta para reducir su monto de pago y mejorar el flujo de caja',

      // Persona messages
      'persona.controller.welcome': `¡Bienvenido! Encontré ${DEMO_PAYMENT_ACCELERATION_FIGURE} en oportunidades de aceleración de pagos`,
      'persona.controller.insight': 'Su DSO es 15 días más alto que el promedio de la industria',
      'persona.controller.action': 'Revisar recomendaciones de aceleración de pagos',
      'persona.analyst.welcome':
        '¡Bienvenido! He identificado oportunidades de optimización transfronteriza',
      'persona.analyst.insight':
        'El análisis de exposición FX muestra $2,340 en ahorros potenciales',
      'persona.analyst.action': 'Analizar patrones de pagos internacionales',
      'persona.ceo.welcome': '¡Bienvenido! He identificado mejoras de calificación de contratos',
      'persona.ceo.insight':
        'Los indicadores de fortaleza financiera muestran preparación para contratos más grandes',
      'persona.ceo.action': 'Revisar oportunidades de crecimiento estratégico',
      'persona.cfo.welcome': '¡Bienvenido! He preparado su resumen financiero ejecutivo',
      'persona.cfo.insight':
        'Las proyecciones de flujo de caja muestran 23% de potencial de mejora',
      'persona.cfo.action': 'Revisar recomendaciones financieras estratégicas',

      // Business outcomes
      'business.getPaidFaster': 'Cobre hasta 60 Días Más Rápido',
      'business.growCrossBorder': 'Crezca Ingresos Transfronterizos',
      'business.qualifyContracts': 'Califique para Contratos Más Grandes',
      'business.getPaidFasterDesc':
        'Aceleración de pagos impulsada por IA para controladores de manufactura',
      'business.growCrossBorderDesc': 'Oportunidades de expansión internacional para analistas',
      'business.qualifyContractsDesc': 'Posicionamiento de crecimiento estratégico para liderazgo',
      'business.opportunity': 'Oportunidad de Negocio',
      'business.potentialValue': 'valor potencial',

      // Controllers
      'controllers.tellTale': 'Tell Tale - Analizador de Email',
      'controllers.fullSend': 'Full Send - Orquestador QTC',
      'controllers.poniente': 'Poniente - Estabilizador de Flujo de Caja',
      'controllers.levante': 'Levante - Gestor de Riesgo Crediticio',
      'controllers.configure': 'Configurar Controladores',

      // Contracts
      'contracts.upload': 'Subir',
      'contracts.analysis': 'Análisis',
      'contracts.creditScoring': 'Puntuación de Crédito',
      'contracts.dataManagement': 'Gestión de Datos',
      'contracts.earlyPayment': 'Pago Anticipado',
      'contracts.language': 'Idioma',
      'pages.contracts': 'Contratos',

      // Common
      'common.loading': 'Cargando...',
      'common.error': 'Error',
      'common.success': 'Éxito',
      'common.cancel': 'Cancelar',
      'common.save': 'Guardar',
      'common.delete': 'Eliminar',
      'common.edit': 'Editar',
      'common.view': 'Ver',
      'common.search': 'Buscar...',
      'common.filter': 'Filtrar',
      'common.sort': 'Ordenar',
      'common.export': 'Exportar',
      'common.import': 'Importar',
      'common.all': 'Todo',
      'common.retry': 'Reintentar',
      'common.add': 'Agregar',

      // Status
      'status.active': 'Activo',
      'status.inactive': 'Inactivo',
      'status.pending': 'Pendiente',
      'status.completed': 'Completado',
      'status.failed': 'Fallido',

      // Messages
      'message.priority.high': 'Prioridad Alta',
      'message.priority.medium': 'Prioridad Media',
      'message.priority.low': 'Prioridad Baja',
      'message.category.invoice': 'Factura',
      'message.category.payment': 'Pago',
      'message.category.general': 'General',

      // Quick Actions
      'action.processPayment': 'Procesar Pago',
      'action.createPO': 'Crear OC',
      'action.markComplete': 'Marcar Completo',
      'action.aiActions': 'Acciones IA',
      'action.aiAnalyze': 'Analizar con IA',
      'action.scheduleFollowup': 'Programar Seguimiento',
      'action.aiEnhanced': 'Mejorado con IA',
      'action.aiInsights': 'Insights IA',
      'action.fullMessage': 'Mensaje Completo',
      'action.highUrgency': 'Alta Urgencia',

      // Buyers
      'buyers.subtitle': 'Gestiona las relaciones con tus clientes y perfiles de crédito',
      'buyers.addBuyer': 'Agregar Comprador',
      'buyers.title': 'Compradores',

      // Chat
      'chat.welcomeMessage':
        '👋 ¡Hola! Puedo ayudarte con correos, operaciones financieras, pagos y soporte. ¿Qué te gustaría saber?',
      'chat.placeholder': 'Pregunta cualquier cosa...',
      'chat.aiAssistant': 'Asistente IA',
      'chat.smart': 'Inteligente',
      'chat.askAnything': 'Pregunta cualquier cosa sobre tu negocio',
      'chat.thinking': 'Pensando...',
      'chat.errorMessage': 'Lo siento, encontré un error. Por favor intenta de nuevo.',
      'chat.dataProcessed': 'Datos procesados por agentes IA',
      // Widget de flujo de efectivo en el panel del asistente (BUG-87 AC2)
      'chat.cashFlowWidget.position': 'Posición de efectivo',
      'chat.cashFlowWidget.net': 'Posición neta',
      'chat.cashFlowWidget.receivables': 'Cuentas por cobrar',
      'chat.cashFlowWidget.payables': 'Cuentas por pagar',
      'chat.cashFlowWidget.overdue': 'Vencido',
      'chat.cashFlowWidget.viewDetail': 'Ver detalle del flujo de efectivo',
      'chat.quickQuery1': '¿Cuál es mi posición de efectivo?',
      'chat.quickQuery2': 'Procesar mis correos',
      'chat.quickQuery3': 'Ayúdame con pagos',
      'chat.quickQuery4': 'Mostrar facturas vencidas',

      // Priorities
      'priorities.cashFlowManagement': 'Gestión de Flujo de Caja',
      'priorities.paymentAcceleration': 'Aceleración de Pagos',

      // Insights
      'insights.cashFlowOptimization': 'Optimización de Flujo de Caja',
      'insights.paymentTermsAnalysis': 'Análisis de Términos de Pago',

      // Recommendations
      'recommendations.automateInvoiceProcessing': 'Automatizar Procesamiento de Facturas',
      'recommendations.optimizePaymentTerms': 'Optimizar Términos de Pago',

      // Receivables Table
      'receivables.table.invoiceNumber': 'Factura #',
      'receivables.table.customer': 'Cliente',
      'receivables.table.amount': 'Monto',
      'receivables.table.dueDate': 'Fecha de Vencimiento',
      'receivables.table.alisioForecast': 'Pronóstico Alisio',
      'receivables.table.predictedPay': 'Pago Predicho',
      'receivables.table.status': 'Estado',
      'receivables.table.riskScore': 'Puntuación de Riesgo',
      'receivables.table.daysOverdue': 'Días Vencidos',
      'receivables.table.actions': 'Acciones',

      // KPI Card
      'kpi.financialIntelligence': 'Inteligencia Financiera',
      'kpi.liveDataAnalysis': 'Análisis de Datos en Vivo',
      'kpi.loading': 'Cargando KPIs...',
      'kpi.error': 'Error al cargar KPIs',
      'kpi.metricsTracked': 'métricas clave rastreadas',
      'kpi.realtimeIntelligence': 'Inteligencia financiera en tiempo real',
      'kpi.allSystemsActive': 'Todos los sistemas activos',
      'kpi.priority': 'prioridad',

      // Budget vs Actuals
      'bva.tooltip': 'Compare su presupuesto planificado con el rendimiento real',
      'bva.line': 'Línea',
      'bva.bar': 'Barra',
      'bva.weekly': 'Semanal',
      'bva.monthly': 'Mensual',
      'bva.quarterly': 'Trimestral',
      'bva.yearly': 'Anual',
      'bva.budgetVsActual': 'Presupuesto vs Real',
      'bva.thisMonthBudget': 'Presupuesto Este Mes',
      'bva.thisMonthActual': 'Real Este Mes',
      'bva.variance': 'Variación',
      'bva.averageVariance': 'Variación Promedio 6 Meses:',
      'bva.performingAbove': 'Rendimiento superior al presupuesto',
      'bva.performingBelow': 'Rendimiento inferior al presupuesto',

      // Dashboard Sections
      'dashboard.recommended': 'Recomendado',
      'dashboard.addCharts': 'Agregar Gráficos',
      'dashboard.agentRecommendations': 'Recomendaciones de Agentes',
      'dashboard.allCharts': 'Todos los Gráficos',

      // KPI Metrics
      'metrics.keyMetrics': 'Métricas Clave',
      'metrics.essentialIndicators': 'Indicadores de rendimiento esenciales',
      'metrics.cashPosition': 'Posición de Efectivo',
      'metrics.paymentEfficiency': 'Eficiencia de Pagos',
      'metrics.compliance': 'Cumplimiento',
      'metrics.improving': 'Mejorando',
      'metrics.onTarget': 'En Objetivo',
      'metrics.excellent': 'Excelente',
      'metrics.attention': 'Atención',
      'metrics.neutral': 'Neutral',
      'metrics.onTrack': 'En camino',
      'metrics.current': 'Actual',
      'metrics.summary': 'Resumen:',
      'metrics.allPerforming':
        'Todas las métricas clave están funcionando bien. Posición de efectivo mejorando, pagos en camino, cumplimiento al día.',

      // Recommendations
      'recommendations.smartRecommendations': 'Recomendaciones Inteligentes',
      'recommendations.actionsToOptimize': 'Acciones para optimizar su posición financiera',
      'recommendations.updated': 'Actualizado',
      'recommendations.roleFocus': 'Prioridad para su rol',
      'recommendations.period30d': '30 días',
      'recommendations.period90d': '90 días',
      'recommendations.period12m': '12 meses',
      'recommendations.quickWin': 'GANANCIA RÁPIDA',
      'recommendations.strategic': 'ESTRATÉGICO',
      'recommendations.automated': 'AUTOMATIZADO',
      'recommendations.impact': 'Impacto:',
      'recommendations.available': 'recomendaciones disponibles.',
      'recommendations.quickWinsSave': 'Las ganancias rápidas pueden ahorrar',
      'recommendations.strategicFreeUp': 'Las acciones estratégicas pueden liberar',
      'recommendations.immediately': 'inmediatamente.',

      // Recommendation 1
      'rec1.title': 'Tomar Descuentos por Pago Anticipado',
      'rec1.description':
        'Pague a proveedores anticipadamente para capturar descuentos del 2% en facturas de $280K',
      'rec1.impact': 'Ahorre $5,600 inmediatamente',
      'rec1.timeEstimate': '5 minutos',
      'rec1.action': 'Revisar y Pagar',

      // Recommendation 2
      'rec2.title': 'Retrasar Compra de Equipo',
      'rec2.description':
        'Posponer compra de equipo de $340K por 30 días para mejorar el flujo de caja',
      'rec2.impact': 'Liberar $340K para capital de trabajo',
      'rec2.timeEstimate': '15 minutos',
      'rec2.action': 'Reprogramar',

      // Recommendation 3
      'rec3.title': 'Configurar Recordatorios de Pago',
      'rec3.description': 'Automatizar recordatorios de pago para clientes con facturas vencidas',
      'rec3.impact': 'Reducir DSO en 5-7 días',
      'rec3.timeEstimate': '2 minutos',
      'rec3.action': 'Habilitar',

      // Recommendation 4
      'rec4.title': 'Revisar Términos de Crédito',
      'rec4.description':
        'Negociar mejores términos de pago con proveedores basado en historial de pagos',
      'rec4.impact': 'Extender términos de pago en 15 días',
      'rec4.timeEstimate': '10 minutos',
      'rec4.action': 'Contactar Proveedores',

      // Payables
      'payables.controller': 'Controlador',
      'payables.financeOps': 'Operaciones Financieras',
      'payables.learningJourney': 'Viaje de Aprendizaje',
      'payables.createPayment': 'Crear Pago',
      'payables.totalBills': 'Total de Facturas',
      'payables.pending': 'Pendiente',
      'payables.overdue': 'Vencido',
      'payables.potentialSavings': 'Ahorros Potenciales',
      'payables.earlyPaymentDiscounts': 'Descuentos por pago anticipado',
      'payables.payablesFinancing': 'Financiamiento de Cuentas por Pagar',
      'payables.optimizeWorkingCapital':
        'Optimice su capital de trabajo con soluciones de pago flexibles',
      'payables.newFeature': 'Nueva Función',
      'payables.extendedPaymentTerms': 'Plazos de Pago Extendidos',
      'payables.availableCredit': 'Crédito Disponible',
      'payables.applyForFinancing': 'Solicitar Financiamiento',
      'payables.viewOptions': 'Ver Opciones',
      'payables.billsOverdue': 'facturas vencidas',
      'payables.billOverdue': 'factura vencida',
      'payables.totalOverdueAmount': 'Monto total vencido',
      'payables.review': 'Revisar',
      'payables.billsAndPayments': 'Facturas y Pagos',
      'payables.managePayables':
        'Administre sus cuentas por pagar con conocimientos inteligentes y automatización',
      'payables.table.billNumber': 'Número de Factura',
      'payables.table.supplier': 'Proveedor',
      'payables.table.amount': 'Monto',
      'payables.table.dueDate': 'Fecha de Vencimiento',
      'payables.table.status': 'Estado',
      'payables.table.predictedPay': 'Pago Previsto',
      'payables.table.savings': 'Ahorros',
      'payables.table.terms': 'Términos',
      'payables.table.automation': 'Automatización',
      'payables.table.actions': 'Acciones',
      'payables.table.search': 'Buscar facturas...',
      'payables.table.noBills': 'No se encontraron facturas que coincidan con su búsqueda.',
      'payables.table.daysOverdue': 'Días Vencidos',
      'payables.status.pending': 'Pendiente',
      'payables.status.overdue': 'Vencido',
      'payables.status.paid': 'Pagado',
      'payables.status.scheduled': 'Programado',
      'payables.status.draft': 'Borrador',
      'payables.status.approved': 'Aprobado',
      'payables.noBills': 'No se encontraron facturas',
      'payables.noBillsDescription':
        'Las facturas aparecerán aquí una vez que se creen o importen desde su sistema contable.',
      'payables.createFirstBill': 'Crear una Factura',
      'payables.managePayablesWithInsights':
        'Administre sus cuentas por pagar con conocimientos inteligentes y automatización',
      'payables.paymentTermsDays': '30-90 días',
      'payables.discountRange': '2-5%',
      'payables.availableCreditAmount': '$50K+',
      'payables.earlyPaymentDiscountsLabel': 'Descuentos por Pago Anticipado',
      'payables.availableCreditLabel': 'Crédito Disponible',
      'payables.dpo': 'DPO (Días por Pagar)',

      // Payments In
      'paymentsIn.overview': 'Resumen',
      'paymentsIn.recentPayments': 'Pagos Recientes',
      'paymentsIn.virtualAccounts': 'Cuentas Virtuales',
      'paymentsIn.reconciliation': 'Reconciliación',
      'paymentsIn.totalReceived': 'Total Recibido',
      'paymentsIn.pendingPayments': 'Pagos Pendientes',
      'paymentsIn.activeCustomers': 'Clientes Activos',
      'paymentsIn.multiCurrency': 'Multi-Moneda',
      'paymentsIn.activeVirtualAccounts': 'Cuentas virtuales activas',
      'paymentsIn.virtualAccountsSummary': 'Resumen de Cuentas Virtuales',
      'paymentsIn.usdAccount': 'Cuenta USD',
      'paymentsIn.eurAccount': 'Cuenta EUR',
      'paymentsIn.gbpAccount': 'Cuenta GBP',
      'paymentsIn.paymentReconciliation': 'Reconciliación de Pagos',
      'paymentsIn.unreconciledPayments': 'Pagos No Reconciliados',
      'paymentsIn.paymentsNeedMatching': 'pagos necesitan conciliación',
      'paymentsIn.reconcile': 'Reconciliar',
      'paymentsIn.fromLastMonth': 'del mes pasado',
      'paymentsIn.paymentsPending': 'pagos pendientes',
      'paymentsIn.received': 'Recibido',
      'paymentsIn.ago': 'hace',
      'paymentsIn.hoursAgo': 'horas',
      'paymentsIn.dayAgo': 'día',
      'paymentsIn.daysAgo': 'días',

      // Component Titles
      'component.keyMetrics': 'Métricas Clave',
      'component.dataSources': 'Fuentes de Datos',
      'component.prioritySupport': 'Soporte Prioritario',
      'component.cashPosition': 'Posición de Efectivo',
      'component.waterfallCashFlow': 'Flujo de Efectivo en Cascada',
      'component.laborCosts': 'Costos Laborales (Line Type)',
      'component.budgetVsActuals': 'Presupuesto vs Real',
      'component.scenarioPlanning': 'Planificación de Escenarios',
      'component.availableCredit': 'Crédito Disponible',
      'component.whatIfSimulation': 'Simulación ¿Qué Pasaría Si?',
      'component.alertsNotifications': 'Alertas y Notificaciones',
      'component.smartRecommendations': 'Recomendaciones Inteligentes',
      'component.keyInsights': 'Perspectivas Clave',
      'component.approvalManagement': 'Gestión de Aprobaciones',
      'component.approvalSummary': 'Resumen de Aprobaciones',
      'component.billsOverview': 'Resumen de Facturas',
      'component.latePaymentTracker': 'Rastreador de Pagos Tardíos',
      'component.roiCalculator': 'Calculadora de ROI',
      'component.paymentAnalysisGrid': 'Cuadrícula de Análisis de Pagos',
      'component.crossBorderAnalysis': 'Análisis Transfronterizo',
      'component.complianceMonitoring': 'Monitoreo de Cumplimiento',
      'component.cashForecastPlanning': 'Planificación de Pronóstico de Efectivo',
      'component.vendorPaymentOptimization': 'Optimización de Pagos a Proveedores',
      'component.dsoGauge': 'Indicador DSO',
      'component.cashTrend': 'Tendencia de Efectivo',
      'component.kpiTrend': 'Tendencia de KPI',
      'component.cashForecast': 'Pronóstico de Efectivo',
      'component.cashWaterfall': 'Cascada de Efectivo',
      'component.fxExposure': 'Exposición Cambiaria',

      // Dashboard Cards
      'dashboard.cashPosition': 'Posición de Efectivo',
      'dashboard.dueThisWeek': 'Vence Esta Semana',
      'dashboard.collectible': 'Cobrable',
      'dashboard.cashPinchExpected': 'Escasez de efectivo esperada Día',
      'dashboard.receivablesHealth': 'Salud de Cuentas por Cobrar',
      'dashboard.daysDSO': 'días DSO',
      'dashboard.totalAR': 'Total CxC',
      'dashboard.atRisk': 'En Riesgo (61+ días)',
      'dashboard.opportunities': 'Oportunidades',
      'dashboard.found': 'encontradas',
      'dashboard.utilization': 'Utilización',
      'dashboard.used': 'Usado',
      'dashboard.creditLineLabel': 'Línea',

      // Dashboard Header
      'dashboard.financialOverview': 'Su resumen financiero e insights',
      'dashboard.match': 'coincidencia',
      'dashboard.components': 'Componentes',
      'dashboard.configure': 'Configurar',
      'dashboard.insights': 'Perspectivas',

      // Context Insights
      'dashboard.systemPerformance': 'Rendimiento del Sistema para',
      'dashboard.efficiency': 'Eficiencia',
      'dashboard.automation': 'Automatización',
      'dashboard.intelligenceProfile': 'Perspectivas del Perfil de Inteligencia',
      'dashboard.smartRecommendations': 'Recomendaciones Inteligentes',
      'dashboard.impact': 'impacto',
      'dashboard.activeComponentsTitle': 'Componentes Activos',
      'dashboard.componentsActive': 'componentes actualmente activos',
      'dashboard.controllerProfile': 'Perfil de Controlador',
      'dashboard.optimizedLayout': 'Diseño de panel optimizado para su rol',
      'dashboard.geographicComplexity': 'Complejidad Geográfica',
      'dashboard.multiCurrencyEnabled': 'Funciones multi-moneda habilitadas',
      'dashboard.automationOpportunity': 'Oportunidad de Automatización',
      'dashboard.workflowAutomation': 'Potencial para automatización de flujos de trabajo',
      'dashboard.excelDependency': 'Dependencia de Excel',
      'dashboard.excelGridPrioritized': 'Componentes de cuadrícula tipo Excel priorizados',
      'dashboard.enableDualMode': 'Habilitar Gráficos de Doble Modo',
      'dashboard.dualModeDesc':
        'Cambiar entre vistas de gráfico y Excel para análisis más profundo',
      'dashboard.enableNow': 'Habilitar Ahora',
      'dashboard.activateFormulas': 'Activar Fórmulas de Agente',
      'dashboard.formulasDesc':
        'Usar fórmulas potenciadas por IA en componentes de hoja de cálculo',
      'dashboard.activate': 'Activar',
      'dashboard.crossBorderOpt': 'Optimización Transfronteriza',
      'dashboard.crossBorderDesc': 'Optimizar operaciones multi-moneda con Windrose',
      'dashboard.complianceMonitoring': 'Monitoreo Automatizado de Cumplimiento',
      'dashboard.complianceDesc':
        'Configurar HookedIn para seguimiento de cumplimiento regulatorio',
      'dashboard.setup': 'Configurar',
      'dashboard.productivity': 'Productividad',
      'dashboard.intelligence': 'Inteligencia',
      'dashboard.operations': 'Operaciones',
      'dashboard.complianceCategory': 'Cumplimiento',

      // Alert Accordion
      'dashboard.alert.dsoIncreased': 'DSO Aumentó 11 Días',
      'dashboard.alert.dsoDesc': 'Impacta liquidez en -9 días',
      'dashboard.alert.dsoRec': 'Ejecutar análisis de proyección de efectivo',
      'dashboard.alert.buyerCreditLimit': 'Comprador Z Cerca del Límite de Crédito',
      'dashboard.alert.buyerCreditDesc': '92% de línea de crédito asegurada utilizada',
      'dashboard.alert.buyerCreditRec': 'Solicitar revisión de límite Allianz',
      'dashboard.alert.paymentAnomaly': 'Anomalía en Patrón de Pago',
      'dashboard.alert.paymentAnomalyDesc': 'Cliente XYZ muestra comportamiento de pago irregular',
      'dashboard.alert.paymentAnomalyRec': 'Revisar términos de crédito',
      'dashboard.alert.docVerification': 'Verificación de Documentos Requerida',
      'dashboard.alert.docVerificationDesc': 'Múltiples facturas pendientes de verificación',
      'dashboard.alert.docVerificationRec': 'Revisar requisitos de documentos',
      'dashboard.alert.earlyPaymentOpp': 'Oportunidad de Pago Anticipado',
      'dashboard.alert.earlyPaymentDesc': 'Descuento potencial del 2% disponible',
      'dashboard.alert.earlyPaymentRec': 'Considerar pago anticipado',
      'dashboard.alert.creditRatingUpdated': 'Calificación Crediticia Actualizada',
      'dashboard.alert.creditRatingDesc': 'Puntaje crediticio del cliente ABC mejoró',
      'dashboard.alert.creditRatingRec': 'Revisar límites de crédito',
      'dashboard.alert.cashFlow': 'Flujo de Caja',
      'dashboard.alert.creditRisk': 'Riesgo Crediticio',
      'dashboard.alert.riskManagement': 'Gestión de Riesgos',
      'dashboard.alert.optimization': 'Optimización',
      'dashboard.alert.information': 'Información',
      'dashboard.alert.agent': 'Agente',
      'dashboard.alert.priority': 'Prioridad',
      'dashboard.alert.status': 'Estado',
      'dashboard.alert.recommendation': 'Recomendación',

      // Spreadsheet Overlay
      'dashboard.accessRestricted': 'Acceso Restringido',
      'dashboard.noPermission':
        'No tiene permiso para acceder al refinamiento de Métricas Windrose. Por favor contacte a su administrador.',
      'dashboard.windroseSaved': 'Métricas Windrose Actualizadas',
      'dashboard.saveSuccess':
        'Los refinamientos de métricas del agente se han guardado exitosamente.',
      'dashboard.saveFailed':
        'Error al guardar refinamientos de métricas. Por favor intente de nuevo.',
      'dashboard.simulationMode': 'Modo Simulación Windrose',
      'dashboard.simulationActive':
        'Modo simulación activo - Las salidas de Windrose usarán valores refinados',
      'dashboard.activeRefinements': 'Refinamientos Activos',
      'dashboard.refinementSettings': 'Configuración de Refinamientos',
      'dashboard.metricRefinements': 'Refinamientos de Métricas Windrose',
      'dashboard.reset': 'Restablecer',
      'dashboard.saving': 'Guardando...',
      'dashboard.save': 'Guardar',
      'dashboard.permissionScope': 'Alcance de Permisos',
      'dashboard.autoBackup': 'Frecuencia de Respaldo Automático',
      'dashboard.everyHour': 'Cada Hora',
      'dashboard.daily': 'Diario',
      'dashboard.weekly': 'Semanal',
      'dashboard.tableAgent': 'Agente',
      'dashboard.tableMetric': 'Métrica',
      'dashboard.tableOriginal': 'Original',
      'dashboard.tableRefined': 'Valor Refinado',
      'dashboard.tableEnabled': 'Habilitado',

      // Forex Dashboard
      'forex.title': 'FX y Cuentas Virtuales',
      'forex.subtitle': 'Gestionar cuentas virtuales, tasas FX y conversiones de moneda',
      'forex.lastUpdated': 'Última actualización:',
      'forex.refresh': 'Actualizar',
      'forex.totalBalance': 'Balance Total',
      'forex.fxExposure': 'Exposición FX',
      'forex.dailyPL': 'P&L Diario',
      'forex.activeHedges': 'Coberturas Activas',
      'forex.virtualAccounts': 'Cuentas Virtuales',
      'forex.fxRates': 'Tasas FX',
      'forex.transactions': 'Transacciones',
      'forex.hedging': 'Cobertura',

      // Onboarding
      'onboarding.welcomeTitle': 'Bienvenido a Alisio',
      'onboarding.welcomeSubtitle': 'Tu plataforma de operaciones financieras con IA',
      'onboarding.yourProfile': 'Tu Perfil',
      'onboarding.role': 'Rol',
      'onboarding.companySize': 'Tamaño de empresa',
      'onboarding.industry': 'Industria',
      'onboarding.company': 'Empresa',
      'onboarding.whatsNext': '¿Qué sigue?',
      'onboarding.whatsNextDesc':
        'Te ayudaremos a configurar las prioridades de tu IA según tu rol y necesidades de negocio.',
      'onboarding.step1Title': 'Configura tus Prioridades',
      'onboarding.step1Desc': 'Elige en qué áreas la IA debe enfocarse más',
      'onboarding.step2Title': 'Conoce a tu Equipo de IA',
      'onboarding.step2Desc': 'Te presentamos a tus agentes de IA personalizados',
      'onboarding.step3Title': 'Comienza a Usar Alisio',
      'onboarding.step3Desc': 'Empieza a optimizar tus operaciones financieras',
      'onboarding.setupProgress': 'Progreso de Configuración',
      'onboarding.stepOf': 'Paso {current} de {total}',
      'onboarding.back': 'Atrás',
      'onboarding.continue': 'Continuar',
      'onboarding.skip': 'Omitir',
      'onboarding.getStarted': 'Comenzar con Alisio',
      'onboarding.setupComplete': '¡Configuración Completa!',
      'onboarding.setupCompleteDesc':
        'Tu equipo de IA está listo para ayudarte a optimizar tus operaciones financieras',
      'onboarding.yourConfig': 'Tu Configuración',
      'onboarding.prioritySettings': 'Configuración de Prioridades',
      'onboarding.whatYouCanDo': 'Lo que Puedes Hacer Ahora',
      'onboarding.viewMessages': 'Ver Mensajes por Prioridad',
      'onboarding.viewMessagesDesc': 'Ve insights de IA organizados por tus prioridades',
      'onboarding.adjustSettings': 'Ajustar Configuración',
      'onboarding.adjustSettingsDesc': 'Modifica prioridades cuando quieras en Configuración',
      'onboarding.connectSystems': 'Conecta tus Sistemas',
      'onboarding.connectSystemsDesc': 'Integra con tus herramientas financieras existentes',

      // Tell Tale email widget (BUG-114) — ported from the inline dict during merge.
      'telltale.recentEmails': 'Correos Recientes',
      'telltale.total': 'en total',
      'telltale.unread': 'sin leer',
      'telltale.noEmails': 'No hay correos para mostrar',
      'telltale.refreshInbox': 'Actualizar Bandeja',
      'telltale.refresh': 'Actualizar',
      'telltale.processAll': 'Procesar Todos',
      'telltale.viewFullInbox': 'Ver Bandeja Completa',
      'telltale.view': 'Ver',
      'telltale.process': 'Procesar',
      'telltale.cashImpact': 'Impacto en Efectivo',
      'telltale.extracted': 'Extraído',
      'telltale.amount': 'Monto',
      'telltale.due': 'Vence',
      'telltale.actionRequired': 'Requiere Acción',
      'telltale.recently': 'Recientemente',
      'telltale.justNow': 'Justo ahora',
      'telltale.hoursAgo': ' h atrás',
      'telltale.daysAgo': ' d atrás',
      'telltale.insight': 'Observación de Tell Tale',
      'telltale.processedPrefix': 'He procesado',
      'telltale.processedSuffix': 'correos automáticamente.',
      'telltale.needAttention': 'correos requieren tu atención.',
      'telltale.foundFinancialData':
        'Encontré datos financieros que podrían afectar el flujo de caja.',

      // Payables action labels + unavailable notice (BUG-109) — ported during merge.
      'payables.actions.schedulePayment': 'Programar Pago',
      'payables.actions.schedule': 'Programar',
      'payables.actions.earlyPayment': 'Pago Anticipado',
      'payables.actions.savingsSuffix': 'de ahorro',
      'payables.actions.negotiateTerms': 'Negociar Términos',
      'payables.actions.unavailableBadge': 'No disponible',
      'payables.actions.unavailableTitle': 'Acción aún no disponible',
      'payables.actions.unavailableDescription':
        'Esta acción aún no está disponible. No se ha programado, pagado ni enviado nada.',
    },
    'fr-ca': {
      // Charts and Data
      'chart.cashPosition': 'Position de Trésorerie',
      'chart.balanceSheet': 'Bilan',
      'chart.incomeStatement': 'État des Résultats',
      'chart.netCashPosition': 'Position Nette de Trésorerie',
      'chart.assets': 'Actifs',
      'chart.liabilities': 'Passifs',
      'chart.revenue': 'Revenus',
      'chart.expenses': 'Dépenses',
      'chart.profit': 'Profit',
      'chart.budget': 'Budget',
      'chart.actual': 'Réel',
      'chart.forecast': 'Prévision',
      'chart.variance': 'Variance',
      'chart.cashBalance': 'Solde de Trésorerie',
      'chart.moneyIn': 'Argent Entrant',
      'chart.moneyOut': 'Argent Sortant',
      'chart.days': 'Jours',
      'chart.weeks': 'Semaines',
      'chart.months': 'Mois',
      'chart.quarters': 'Trimestres',
      'chart.years': 'Années',
      'chart.today': "Aujourd'hui",
      'chart.yAxisLabel': 'Montant',
      'chart.xAxisLabel': 'Période',
      'chart.noData': 'Aucune donnée disponible',
      'chart.loading': 'Chargement des données...',
      'chart.error': 'Erreur de chargement',

      // Agents and AI
      'agent.processing': 'Traitement...',
      'agent.thinking': 'IA réfléchit...',
      'agent.complete': 'Analyse terminée',
      'agent.error': "Erreur d'analyse IA",
      'agent.recommendation': 'Recommandation',
      'agent.confidence': 'Confiance',
      'agent.suggestion': 'Suggestion',

      // Navigation
      'nav.dashboard': 'Tableau de Bord',
      'nav.messages': 'Messages',
      'nav.analytics': 'Analytiques',
      'nav.settings': 'Paramètres',
      'nav.receivables': 'Comptes Clients',
      'nav.payables': 'Comptes Fournisseurs',
      'nav.contracts': 'Contrats',
      'nav.keyChanges': 'Changements Clés',
      'nav.whatsDrivingIt': 'Ce qui le Motive',
      'nav.takeAction': "Passer à l'Action",
      'nav.allMessages': 'Tous les Messages',
      'nav.priorities': 'Priorités',
      'nav.prioritySettings': 'Paramètres de Priorité',
      'nav.priorityAnalysis': 'Analyse',
      'nav.categories': 'Catégories',
      'nav.allContracts': 'Tous les Contrats',
      'nav.upload': 'Télécharger',
      'nav.analysis': 'Analyse',
      'nav.invoices': 'Factures',
      'nav.credits': 'Crédits',
      'nav.reports': 'Rapports',
      'nav.dataExport': 'Exportation de Données',
      'nav.suppliers': 'Fournisseurs',
      'nav.approvals': 'Approbations',
      'nav.paymentsOut': 'Paiements Sortants',
      'nav.paymentsIn': 'Paiements Entrants',
      'nav.reconciliation': 'Réconciliation',
      'nav.trends': 'Tendances',
      'nav.batchPayments': 'Paiements par Lot',
      'nav.vendorManagement': 'Gestion des Fournisseurs',
      'nav.general': 'Général',
      'nav.users': 'Utilisateurs',
      'nav.integrations': 'Intégrations',
      'nav.overview': 'Aperçu',
      'nav.details': 'Détails',
      'nav.actions': 'Actions',
      'nav.buyers': 'Acheteurs',
      'nav.aiAssistant': 'Assistant IA',
      'nav.aiAssistantDesc': "Chattez avec les agents IA pour l'assistance financière",

      // Dashboard
      'dashboard.welcomeBack': 'Bon retour',
      'dashboard.availableCredit': 'Crédit Disponible',
      'dashboard.creditUtilized': 'Crédit Utilisé',
      'dashboard.creditLine': 'Ligne de Crédit',
      'dashboard.scheduled': 'Programmé',
      'dashboard.paid': 'Payé',
      'dashboard.dataSources': 'Sources de Données',
      'dashboard.activeAgents': 'Agents Actifs',
      'dashboard.docsProcessed': 'docs traités',
      'dashboard.agentMonitorMessage':
        'Les agents surveillent continuellement vos documents financiers',
      'dashboard.goDeeperToControllers': 'Approfondir les Contrôleurs',

      // Personas
      'persona.financialController': 'Contrôleur Financier',
      'persona.analyst': 'Analyste',
      'persona.ceo': 'PDG',
      'persona.cfo': 'Directeur Financier',

      // Dashboard sections
      'sections.keyChanges': 'Changements Clés',
      'sections.whatsDrivingIt': 'Ce qui le Pousse',
      'sections.takeAction': 'Prendre Action',
      'sections.keyChangesSubtitle':
        'Mises à jour importantes et métriques nécessitant votre attention',
      'sections.whatsDrivingItSubtitle': 'Analyse et insights derrière les chiffres',
      'sections.takeActionSubtitle':
        'Recommandations et prochaines étapes pour améliorer les performances',

      // Payment opportunities
      'payments.saveMoneyEarly': 'Économisez 3 400 $ avec Paiement Anticipé',
      'payments.earlyPaymentDesc': 'Prenez 2% de remise sur les factures fournisseurs',
      'payments.opportunitiesTitle':
        "Opportunités détectées par IA pour économiser de l'argent avec les paiements anticipés",
      'payments.youSaveMoney': "Vous Économisez de l'Argent",
      'payments.saveMoneyDesc':
        "Acceptez l'offre pour réduire votre montant de paiement et améliorer la trésorerie",

      // Persona messages
      'persona.analyst.welcome':
        "Bienvenue! J'ai identifié des opportunités d'optimisation transfrontalière",
      'persona.analyst.insight':
        "L'analyse d'exposition FX montre 2 340 $ d'économies potentielles",
      'persona.analyst.action': 'Analyser les modèles de paiements internationaux',
      'persona.ceo.welcome':
        "Bienvenue! J'ai identifié des améliorations de qualification de contrats",
      'persona.ceo.insight':
        'Les indicateurs de solidité financière montrent une préparation pour de plus grands contrats',
      'persona.ceo.action': 'Examiner les opportunités de croissance stratégique',
      'persona.cfo.welcome': "Bienvenue! J'ai préparé votre aperçu financier exécutif",
      'persona.cfo.insight':
        "Les projections de trésorerie montrent 23% de potentiel d'amélioration",
      'persona.cfo.action': 'Examiner les recommandations financières stratégiques',

      // Controllers
      'controllers.tellTale': "Tell Tale - Analyseur d'Email",
      'controllers.fullSend': 'Full Send - Orquestrateur QTC',
      'controllers.poniente': 'Poniente - Stabilisateur de Flux de Trésorerie',
      'controllers.levante': 'Levante - Gestionnaire de Risque Crédit',
      'controllers.configure': 'Configurer les Contrôleurs',

      // Contracts
      'contracts.upload': 'Télécharger',
      'contracts.analysis': 'Analyse',
      'contracts.creditScoring': 'Notation de Crédit',
      'contracts.dataManagement': 'Gestion des Données',
      'contracts.earlyPayment': 'Paiement Anticipé',
      'contracts.language': 'Langue',
      'pages.contracts': 'Contrats',

      // Common
      'common.loading': 'Chargement...',
      'common.error': 'Erreur',
      'common.success': 'Succès',
      'common.cancel': 'Annuler',
      'common.save': 'Sauvegarder',
      'common.delete': 'Supprimer',
      'common.edit': 'Modifier',
      'common.view': 'Voir',
      'common.search': 'Rechercher...',
      'common.filter': 'Filtrer',
      'common.sort': 'Trier',
      'common.export': 'Exporter',
      'common.import': 'Importer',
      'common.all': 'Tout',
      'common.retry': 'Réessayer',
      'common.add': 'Ajouter',

      // Status
      'status.active': 'Actif',
      'status.inactive': 'Inactif',
      'status.pending': 'En Attente',
      'status.completed': 'Terminé',
      'status.failed': 'Échoué',

      // Messages
      'message.priority.high': 'Priorité Haute',
      'message.priority.medium': 'Priorité Moyenne',
      'message.priority.low': 'Priorité Basse',
      'message.category.invoice': 'Facture',
      'message.category.payment': 'Paiement',
      'message.category.general': 'Général',

      // Buyers
      'buyers.subtitle': 'Gérez vos relations clients et profils de crédit',
      'buyers.addBuyer': 'Ajouter Acheteur',
      'buyers.title': 'Acheteurs',

      // Chat
      'chat.welcomeMessage':
        '👋 Salut! Je peux vous aider avec les emails, opérations financières, paiements et support. Que souhaitez-vous savoir?',
      'chat.placeholder': "Demandez n'importe quoi...",
      'chat.aiAssistant': 'Assistant IA',
      'chat.smart': 'Intelligent',
      'chat.askAnything': "Demandez n'importe quoi sur votre entreprise",
      'chat.thinking': 'Réflexion...',
      'chat.errorMessage': "Désolé, j'ai rencontré une erreur. Veuillez réessayer.",
      'chat.dataProcessed': 'Données traitées par agents IA',
      // Widget de flux de trésorerie du panneau assistant (BUG-87 AC2)
      'chat.cashFlowWidget.position': 'Position de trésorerie',
      'chat.cashFlowWidget.net': 'Position nette',
      'chat.cashFlowWidget.receivables': 'Créances',
      'chat.cashFlowWidget.payables': 'Dettes fournisseurs',
      'chat.cashFlowWidget.overdue': 'En retard',
      'chat.cashFlowWidget.viewDetail': 'Voir le détail de la trésorerie',
      'chat.quickQuery1': 'Quelle est ma position de trésorerie?',
      'chat.quickQuery2': 'Traiter mes emails',
      'chat.quickQuery3': 'Aidez-moi avec les paiements',
      'chat.quickQuery4': 'Afficher les factures en retard',

      // Priorities
      'priorities.cashFlowManagement': 'Gestion des Flux de Trésorerie',
      'priorities.paymentAcceleration': 'Accélération des Paiements',

      // Insights
      'insights.cashFlowOptimization': 'Optimisation des Flux de Trésorerie',
      'insights.paymentTermsAnalysis': 'Analyse des Conditions de Paiement',

      // Recommendations
      'recommendations.automateInvoiceProcessing': 'Automatiser le Traitement des Factures',
      'recommendations.optimizePaymentTerms': 'Optimiser les Conditions de Paiement',

      // Receivables Table
      'receivables.table.invoiceNumber': 'Facture #',
      'receivables.table.customer': 'Client',
      'receivables.table.amount': 'Montant',
      'receivables.table.dueDate': "Date d'Échéance",
      'receivables.table.alisioForecast': 'Prévision Alisio',
      'receivables.table.predictedPay': 'Paiement Prévu',
      'receivables.table.status': 'Statut',
      'receivables.table.riskScore': 'Score de Risque',
      'receivables.table.daysOverdue': 'Jours de Retard',
      'receivables.table.actions': 'Actions',

      // KPI Card
      'kpi.financialIntelligence': 'Intelligence Financière',
      'kpi.liveDataAnalysis': 'Analyse de Données en Direct',
      'kpi.loading': 'Chargement des KPI...',
      'kpi.error': 'Erreur de chargement des KPI',
      'kpi.metricsTracked': 'métriques clés suivies',
      'kpi.realtimeIntelligence': 'Intelligence financière en temps réel',
      'kpi.allSystemsActive': 'Tous les systèmes actifs',
      'kpi.priority': 'priorité',

      // Budget vs Actuals
      'bva.tooltip': 'Comparez votre budget planifié avec la performance réelle',
      'bva.line': 'Ligne',
      'bva.bar': 'Barre',
      'bva.weekly': 'Hebdomadaire',
      'bva.monthly': 'Mensuel',
      'bva.quarterly': 'Trimestriel',
      'bva.yearly': 'Annuel',
      'bva.budgetVsActual': 'Budget vs Réel',
      'bva.thisMonthBudget': 'Budget Ce Mois',
      'bva.thisMonthActual': 'Réel Ce Mois',
      'bva.variance': 'Écart',
      'bva.averageVariance': 'Écart Moyen 6 Mois:',
      'bva.performingAbove': 'Performance supérieure au budget',
      'bva.performingBelow': 'Performance inférieure au budget',

      // Dashboard Sections
      'dashboard.recommended': 'Recommandé',
      'dashboard.addCharts': 'Ajouter Graphiques',
      'dashboard.agentRecommendations': "Recommandations d'Agents",
      'dashboard.allCharts': 'Tous les Graphiques',

      // KPI Metrics
      'metrics.keyMetrics': 'Métriques Clés',
      'metrics.essentialIndicators': 'Indicateurs de performance essentiels',
      'metrics.cashPosition': 'Position de Trésorerie',
      'metrics.paymentEfficiency': 'Efficacité des Paiements',
      'metrics.compliance': 'Conformité',
      'metrics.improving': 'En Amélioration',
      'metrics.onTarget': 'Sur la Cible',
      'metrics.excellent': 'Excellent',
      'metrics.attention': 'Attention',
      'metrics.neutral': 'Neutre',
      'metrics.onTrack': 'Sur la bonne voie',
      'metrics.current': 'Actuel',
      'metrics.summary': 'Résumé:',
      'metrics.allPerforming':
        'Toutes les métriques clés fonctionnent bien. Position de trésorerie en amélioration, paiements sur la bonne voie, conformité à jour.',

      // Recommendations
      'recommendations.smartRecommendations': 'Recommandations Intelligentes',
      'recommendations.actionsToOptimize': 'Actions pour optimiser votre position financière',
      'recommendations.quickWin': 'VICTOIRE RAPIDE',
      'recommendations.strategic': 'STRATÉGIQUE',
      'recommendations.automated': 'AUTOMATISÉ',
      'recommendations.impact': 'Impact:',
      'recommendations.available': 'recommandations disponibles.',
      'recommendations.quickWinsSave': 'Les victoires rapides peuvent économiser',
      'recommendations.strategicFreeUp': 'Les actions stratégiques peuvent libérer',
      'recommendations.immediately': 'immédiatement.',

      // Recommendation 1
      'rec1.title': 'Profiter des Remises de Paiement Anticipé',
      'rec1.description':
        'Payer les fournisseurs tôt pour capturer des remises de 2% sur 280 000 $ de factures',
      'rec1.impact': 'Économiser 5 600 $ immédiatement',
      'rec1.timeEstimate': '5 minutes',
      'rec1.action': 'Réviser et Payer',

      // Recommendation 2
      'rec2.title': "Reporter l'Achat d'Équipement",
      'rec2.description':
        "Reporter l'achat d'équipement de 340 000 $ de 30 jours pour améliorer le flux de trésorerie",
      'rec2.impact': 'Libérer 340 000 $ pour le fonds de roulement',
      'rec2.timeEstimate': '15 minutes',
      'rec2.action': 'Reprogrammer',

      // Recommendation 3
      'rec3.title': 'Configurer les Rappels de Paiement',
      'rec3.description':
        'Automatiser les rappels de paiement pour les clients avec factures en retard',
      'rec3.impact': 'Réduire le DSO de 5-7 jours',
      'rec3.timeEstimate': '2 minutes',
      'rec3.action': 'Activer',

      // Recommendation 4
      'rec4.title': 'Réviser les Conditions de Crédit',
      'rec4.description':
        "Négocier de meilleures conditions de paiement avec les fournisseurs basé sur l'historique de paiement",
      'rec4.impact': 'Prolonger les conditions de paiement de 15 jours',
      'rec4.timeEstimate': '10 minutes',
      'rec4.action': 'Contacter les Fournisseurs',

      // Payables
      'payables.controller': 'Contrôleur',
      'payables.financeOps': 'Opérations Financières',
      'payables.learningJourney': "Parcours d'Apprentissage",
      'payables.createPayment': 'Créer un Paiement',
      'payables.totalBills': 'Total des Factures',
      'payables.pending': 'En Attente',
      'payables.overdue': 'En Retard',
      'payables.potentialSavings': 'Économies Potentielles',
      'payables.earlyPaymentDiscounts': 'Remises pour paiement anticipé',
      'payables.payablesFinancing': 'Financement des Comptes Fournisseurs',
      'payables.optimizeWorkingCapital':
        'Optimisez votre fonds de roulement avec des solutions de paiement flexibles',
      'payables.newFeature': 'Nouvelle Fonction',
      'payables.extendedPaymentTerms': 'Conditions de Paiement Prolongées',
      'payables.availableCredit': 'Crédit Disponible',
      'payables.applyForFinancing': 'Demander un Financement',
      'payables.viewOptions': 'Voir les Options',
      'payables.billsOverdue': 'factures en retard',
      'payables.billOverdue': 'facture en retard',
      'payables.totalOverdueAmount': 'Montant total en retard',
      'payables.review': 'Examiner',
      'payables.billsAndPayments': 'Factures et Paiements',
      'payables.managePayables':
        "Gérez vos comptes fournisseurs avec des informations intelligentes et l'automatisation",
      'payables.table.billNumber': 'Numéro de Facture',
      'payables.table.supplier': 'Fournisseur',
      'payables.table.amount': 'Montant',
      'payables.table.dueDate': "Date d'Échéance",
      'payables.table.status': 'Statut',
      'payables.table.predictedPay': 'Paiement Prévu',
      'payables.table.savings': 'Économies',
      'payables.table.terms': 'Conditions',
      'payables.table.automation': 'Automatisation',
      'payables.table.actions': 'Actions',
      'payables.table.search': 'Rechercher des factures...',
      'payables.table.noBills': 'Aucune facture trouvée correspondant à vos critères.',
      'payables.table.daysOverdue': 'Jours en Retard',
      'payables.status.pending': 'En Attente',
      'payables.status.overdue': 'En Retard',
      'payables.status.paid': 'Payé',
      'payables.status.scheduled': 'Programmé',
      'payables.status.draft': 'Brouillon',
      'payables.status.approved': 'Approuvé',
      'payables.noBills': 'Aucune facture trouvée',
      'payables.noBillsDescription':
        'Les factures apparaîtront ici une fois créées ou importées de votre système comptable.',
      'payables.createFirstBill': 'Créer une Facture',
      'payables.managePayablesWithInsights':
        "Gérez vos comptes fournisseurs avec des informations intelligentes et l'automatisation",
      'payables.paymentTermsDays': '30-90 jours',
      'payables.discountRange': '2-5%',
      'payables.availableCreditAmount': '50K$+',
      'payables.earlyPaymentDiscountsLabel': 'Remises pour Paiement Anticipé',
      'payables.availableCreditLabel': 'Crédit Disponible',
      'payables.dpo': 'DPO (Jours de Paiement)',

      // Payments In
      'paymentsIn.overview': 'Aperçu',
      'paymentsIn.recentPayments': 'Paiements Récents',
      'paymentsIn.virtualAccounts': 'Comptes Virtuels',
      'paymentsIn.reconciliation': 'Rapprochement',
      'paymentsIn.totalReceived': 'Total Reçu',
      'paymentsIn.pendingPayments': 'Paiements en Attente',
      'paymentsIn.activeCustomers': 'Clients Actifs',
      'paymentsIn.multiCurrency': 'Multi-Devises',
      'paymentsIn.activeVirtualAccounts': 'Comptes virtuels actifs',
      'paymentsIn.virtualAccountsSummary': 'Résumé des Comptes Virtuels',
      'paymentsIn.usdAccount': 'Compte USD',
      'paymentsIn.eurAccount': 'Compte EUR',
      'paymentsIn.gbpAccount': 'Compte GBP',
      'paymentsIn.paymentReconciliation': 'Rapprochement des Paiements',
      'paymentsIn.unreconciledPayments': 'Paiements Non Rapprochés',
      'paymentsIn.paymentsNeedMatching': 'paiements nécessitent un rapprochement',
      'paymentsIn.reconcile': 'Rapprocher',
      'paymentsIn.fromLastMonth': 'du mois dernier',
      'paymentsIn.paymentsPending': 'paiements en attente',
      'paymentsIn.received': 'Reçu',
      'paymentsIn.ago': 'il y a',
      'paymentsIn.hoursAgo': 'heures',
      'paymentsIn.dayAgo': 'jour',
      'paymentsIn.daysAgo': 'jours',

      // Component Titles
      'component.keyMetrics': 'Métriques Clés',
      'component.dataSources': 'Sources de Données',
      'component.prioritySupport': 'Support Prioritaire',
      'component.cashPosition': 'Position de Trésorerie',
      'component.waterfallCashFlow': 'Flux de Trésorerie en Cascade',
      'component.laborCosts': "Coûts de Main-d'œuvre (Line Type)",
      'component.budgetVsActuals': 'Budget vs Réel',
      'component.scenarioPlanning': 'Planification de Scénarios',
      'component.availableCredit': 'Crédit Disponible',
      'component.whatIfSimulation': 'Simulation Et Si',
      'component.alertsNotifications': 'Alertes et Notifications',
      'component.smartRecommendations': 'Recommandations Intelligentes',
      'component.keyInsights': 'Aperçus Clés',
      'component.approvalManagement': 'Gestion des Approbations',
      'component.approvalSummary': 'Résumé des Approbations',
      'component.billsOverview': 'Aperçu des Factures',
      'component.latePaymentTracker': 'Suivi des Paiements en Retard',
      'component.roiCalculator': 'Calculateur de ROI',
      'component.paymentAnalysisGrid': "Grille d'Analyse des Paiements",
      'component.crossBorderAnalysis': 'Analyse Transfrontalière',
      'component.complianceMonitoring': 'Surveillance de la Conformité',
      'component.cashForecastPlanning': 'Planification des Prévisions de Trésorerie',
      'component.vendorPaymentOptimization': 'Optimisation des Paiements aux Fournisseurs',
    },
    'pt-br': {
      // Charts and Data
      'chart.cashPosition': 'Posição de Caixa',
      'chart.balanceSheet': 'Balanço Patrimonial',
      'chart.incomeStatement': 'Demonstração de Resultados',
      'chart.netCashPosition': 'Posição Líquida de Caixa',
      'chart.assets': 'Ativos',
      'chart.liabilities': 'Passivos',
      'chart.revenue': 'Receita',
      'chart.expenses': 'Despesas',
      'chart.profit': 'Lucro',
      'chart.budget': 'Orçamento',
      'chart.actual': 'Real',
      'chart.forecast': 'Previsão',
      'chart.variance': 'Variância',
      'chart.cashBalance': 'Saldo de Caixa',
      'chart.moneyIn': 'Dinheiro Entrando',
      'chart.moneyOut': 'Dinheiro Saindo',
      'chart.days': 'Dias',
      'chart.weeks': 'Semanas',
      'chart.months': 'Meses',
      'chart.quarters': 'Trimestres',
      'chart.years': 'Anos',
      'chart.today': 'Hoje',
      'chart.yAxisLabel': 'Valor',
      'chart.xAxisLabel': 'Período',
      'chart.noData': 'Nenhum dado disponível',
      'chart.loading': 'Carregando dados do gráfico...',
      'chart.error': 'Erro ao carregar dados',

      // Agents and AI
      'agent.processing': 'Processando...',
      'agent.thinking': 'IA está pensando...',
      'agent.complete': 'Análise completa',
      'agent.error': 'Erro na análise da IA',
      'agent.recommendation': 'Recomendação',
      'agent.confidence': 'Confiança',
      'agent.suggestion': 'Sugestão',

      // Navigation
      'nav.dashboard': 'Painel',
      'nav.messages': 'Mensagens',
      'nav.analytics': 'Análises',
      'nav.settings': 'Configurações',
      'nav.receivables': 'Contas a Receber',
      'nav.payables': 'Contas a Pagar',
      'nav.contracts': 'Contratos',
      'nav.keyChanges': 'Mudanças Principais',
      'nav.whatsDrivingIt': 'O que o Impulsiona',
      'nav.takeAction': 'Tomar Ação',
      'nav.allMessages': 'Todas as Mensagens',
      'nav.priorities': 'Prioridades',
      'nav.prioritySettings': 'Configurações de Prioridade',
      'nav.priorityAnalysis': 'Análise',
      'nav.categories': 'Categorias',
      'nav.allContracts': 'Todos os Contratos',
      'nav.upload': 'Carregar',
      'nav.analysis': 'Análise',
      'nav.invoices': 'Faturas',
      'nav.credits': 'Créditos',
      'nav.reports': 'Relatórios',
      'nav.dataExport': 'Exportar Dados',
      'nav.suppliers': 'Fornecedores',
      'nav.approvals': 'Aprovações',
      'nav.paymentsOut': 'Pagamentos Saindo',
      'nav.paymentsIn': 'Pagamentos Entrando',
      'nav.reconciliation': 'Reconciliação',
      'nav.trends': 'Tendências',
      'nav.batchPayments': 'Pagamentos em Lote',
      'nav.vendorManagement': 'Gestão de Fornecedores',
      'nav.general': 'Geral',
      'nav.users': 'Usuários',
      'nav.integrations': 'Integrações',
      'nav.overview': 'Visão Geral',
      'nav.details': 'Detalhes',
      'nav.actions': 'Ações',
      'nav.buyers': 'Compradores',
      'nav.aiAssistant': 'Assistente IA',
      'nav.aiAssistantDesc': 'Converse com agentes IA para assistência financeira',

      // Dashboard
      'dashboard.welcomeBack': 'Bem-vindo de volta',
      'dashboard.availableCredit': 'Crédito Disponível',
      'dashboard.creditUtilized': 'Crédito Utilizado',
      'dashboard.creditLine': 'Linha de Crédito',
      'dashboard.scheduled': 'Agendado',
      'dashboard.paid': 'Pago',
      'dashboard.dataSources': 'Fontes de Dados',
      'dashboard.activeAgents': 'Agentes Ativos',
      'dashboard.docsProcessed': 'docs processados',
      'dashboard.agentMonitorMessage':
        'Agentes monitoram continuamente seus documentos financeiros',
      'dashboard.goDeeperToControllers': 'Aprofundar nos Controladores',

      // Personas
      'persona.financialController': 'Controlador Financeiro',
      'persona.analyst': 'Analista',
      'persona.ceo': 'CEO',
      'persona.cfo': 'CFO',

      // Dashboard sections
      'sections.keyChanges': 'Mudanças Principais',
      'sections.whatsDrivingIt': 'O que está Impulsionando',
      'sections.takeAction': 'Tomar Ação',
      'sections.keyChangesSubtitle': 'Atualizações importantes e métricas que requerem sua atenção',
      'sections.whatsDrivingItSubtitle': 'Análise e insights por trás dos números',
      'sections.takeActionSubtitle': 'Recomendações e próximos passos para melhorar o desempenho',

      // Payment opportunities
      'payments.saveMoneyEarly': 'Economize R$ 3.400 com Pagamento Antecipado',
      'payments.earlyPaymentDesc': 'Obtenha 2% de desconto em faturas de fornecedores',
      'payments.opportunitiesTitle':
        'Oportunidades detectadas por IA para economizar dinheiro com pagamentos antecipados',
      'payments.youSaveMoney': 'Você Economiza Dinheiro',
      'payments.saveMoneyDesc':
        'Aceite a oferta para reduzir o valor do pagamento e melhorar o fluxo de caixa',

      // Persona messages
      'persona.analyst.welcome':
        'Bem-vindo! Identifiquei oportunidades de otimização transfronteiriça',
      'persona.analyst.insight': 'Análise de exposição FX mostra R$ 2.340 em economias potenciais',
      'persona.analyst.action': 'Analisar padrões de pagamentos internacionais',
      'persona.ceo.welcome': 'Bem-vindo! Identifiquei melhorias de qualificação de contratos',
      'persona.ceo.insight':
        'Indicadores de força financeira mostram prontidão para contratos maiores',
      'persona.ceo.action': 'Revisar oportunidades de crescimento estratégico',
      'persona.cfo.welcome': 'Bem-vindo! Preparei sua visão geral financeira executiva',
      'persona.cfo.insight': 'Projeções de fluxo de caixa mostram 23% de potencial de melhoria',
      'persona.cfo.action': 'Revisar recomendações financeiras estratégicas',

      // Controllers
      'controllers.tellTale': 'Tell Tale - Analisador de Email',
      'controllers.fullSend': 'Full Send - Orquestrador QTC',
      'controllers.poniente': 'Poniente - Estabilizador de Fluxo de Caixa',
      'controllers.levante': 'Levante - Gerente de Risco de Crédito',
      'controllers.configure': 'Configurar Controladores',

      // Contracts
      'contracts.upload': 'Carregar',
      'contracts.analysis': 'Análise',
      'contracts.creditScoring': 'Pontuação de Crédito',
      'contracts.dataManagement': 'Gestão de Dados',
      'contracts.earlyPayment': 'Pagamento Antecipado',
      'contracts.language': 'Idioma',
      'pages.contracts': 'Contratos',

      // Common
      'common.loading': 'Carregando...',
      'common.error': 'Erro',
      'common.success': 'Sucesso',
      'common.cancel': 'Cancelar',
      'common.save': 'Salvar',
      'common.delete': 'Excluir',
      'common.edit': 'Editar',
      'common.view': 'Ver',
      'common.search': 'Pesquisar...',
      'common.filter': 'Filtrar',
      'common.sort': 'Ordenar',
      'common.export': 'Exportar',
      'common.import': 'Importar',
      'common.all': 'Todos',
      'common.retry': 'Tentar Novamente',
      'common.add': 'Adicionar',

      // Status
      'status.active': 'Ativo',
      'status.inactive': 'Inativo',
      'status.pending': 'Pendente',
      'status.completed': 'Concluído',
      'status.failed': 'Falhado',

      // Messages
      'message.priority.high': 'Prioridade Alta',
      'message.priority.medium': 'Prioridade Média',
      'message.priority.low': 'Prioridade Baixa',
      'message.category.invoice': 'Fatura',
      'message.category.payment': 'Pagamento',
      'message.category.general': 'Geral',

      // Buyers
      'buyers.subtitle': 'Gerencie seus relacionamentos com clientes e perfis de crédito',
      'buyers.addBuyer': 'Adicionar Comprador',
      'buyers.title': 'Compradores',

      // Chat
      'chat.welcomeMessage':
        '👋 Oi! Posso ajudar com emails, operações financeiras, pagamentos e suporte. O que gostaria de saber?',
      'chat.placeholder': 'Pergunte qualquer coisa...',
      'chat.aiAssistant': 'Assistente IA',
      'chat.smart': 'Inteligente',
      'chat.askAnything': 'Pergunte qualquer coisa sobre seu negócio',
      'chat.thinking': 'Pensando...',
      'chat.errorMessage': 'Desculpe, encontrei um erro. Por favor, tente novamente.',
      'chat.dataProcessed': 'Dados processados por agentes IA',
      // Widget de fluxo de caixa no painel do assistente (BUG-87 AC2)
      'chat.cashFlowWidget.position': 'Posição de caixa',
      'chat.cashFlowWidget.net': 'Posição líquida',
      'chat.cashFlowWidget.receivables': 'Contas a receber',
      'chat.cashFlowWidget.payables': 'Contas a pagar',
      'chat.cashFlowWidget.overdue': 'Vencido',
      'chat.cashFlowWidget.viewDetail': 'Ver detalhe do fluxo de caixa',
      'chat.quickQuery1': 'Qual é minha posição de caixa?',
      'chat.quickQuery2': 'Processar meus emails',
      'chat.quickQuery3': 'Ajude-me com pagamentos',
      'chat.quickQuery4': 'Mostrar faturas vencidas',

      // Priorities
      'priorities.cashFlowManagement': 'Gestão de Fluxo de Caixa',
      'priorities.paymentAcceleration': 'Aceleração de Pagamentos',

      // Insights
      'insights.cashFlowOptimization': 'Otimização de Fluxo de Caixa',
      'insights.paymentTermsAnalysis': 'Análise de Termos de Pagamento',

      // Recommendations
      'recommendations.automateInvoiceProcessing': 'Automatizar Processamento de Faturas',
      'recommendations.optimizePaymentTerms': 'Otimizar Termos de Pagamento',

      // Receivables Table
      'receivables.table.invoiceNumber': 'Fatura #',
      'receivables.table.customer': 'Cliente',
      'receivables.table.amount': 'Valor',
      'receivables.table.dueDate': 'Data de Vencimento',
      'receivables.table.alisioForecast': 'Previsão Alisio',
      'receivables.table.predictedPay': 'Pagamento Previsto',
      'receivables.table.status': 'Status',
      'receivables.table.riskScore': 'Pontuação de Risco',
      'receivables.table.daysOverdue': 'Dias em Atraso',
      'receivables.table.actions': 'Ações',

      // KPI Card
      'kpi.financialIntelligence': 'Inteligência Financeira',
      'kpi.liveDataAnalysis': 'Análise de Dados ao Vivo',
      'kpi.loading': 'Carregando KPIs...',
      'kpi.error': 'Erro ao carregar KPIs',
      'kpi.metricsTracked': 'métricas-chave rastreadas',
      'kpi.realtimeIntelligence': 'Inteligência financeira em tempo real',
      'kpi.allSystemsActive': 'Todos os sistemas ativos',
      'kpi.priority': 'prioridade',

      // Budget vs Actuals
      'bva.tooltip': 'Compare seu orçamento planejado com o desempenho real',
      'bva.line': 'Linha',
      'bva.bar': 'Barra',
      'bva.weekly': 'Semanal',
      'bva.monthly': 'Mensal',
      'bva.quarterly': 'Trimestral',
      'bva.yearly': 'Anual',
      'bva.budgetVsActual': 'Orçamento vs Real',
      'bva.thisMonthBudget': 'Orçamento Este Mês',
      'bva.thisMonthActual': 'Real Este Mês',
      'bva.variance': 'Variação',
      'bva.averageVariance': 'Variação Média 6 Meses:',
      'bva.performingAbove': 'Desempenho acima do orçamento',
      'bva.performingBelow': 'Desempenho abaixo do orçamento',

      // Dashboard Sections
      'dashboard.recommended': 'Recomendado',
      'dashboard.addCharts': 'Adicionar Gráficos',
      'dashboard.agentRecommendations': 'Recomendações de Agentes',
      'dashboard.allCharts': 'Todos os Gráficos',

      // KPI Metrics
      'metrics.keyMetrics': 'Métricas Principais',
      'metrics.essentialIndicators': 'Indicadores de desempenho essenciais',
      'metrics.cashPosition': 'Posição de Caixa',
      'metrics.paymentEfficiency': 'Eficiência de Pagamentos',
      'metrics.compliance': 'Conformidade',
      'metrics.improving': 'Melhorando',
      'metrics.onTarget': 'No Alvo',
      'metrics.excellent': 'Excelente',
      'metrics.attention': 'Atenção',
      'metrics.neutral': 'Neutro',
      'metrics.onTrack': 'No caminho certo',
      'metrics.current': 'Atual',
      'metrics.summary': 'Resumo:',
      'metrics.allPerforming':
        'Todas as métricas principais estão funcionando bem. Posição de caixa melhorando, pagamentos no caminho certo, conformidade atualizada.',

      // Recommendations
      'recommendations.smartRecommendations': 'Recomendações Inteligentes',
      'recommendations.actionsToOptimize': 'Ações para otimizar sua posição financeira',
      'recommendations.quickWin': 'VITÓRIA RÁPIDA',
      'recommendations.strategic': 'ESTRATÉGICO',
      'recommendations.automated': 'AUTOMATIZADO',
      'recommendations.impact': 'Impacto:',
      'recommendations.available': 'recomendações disponíveis.',
      'recommendations.quickWinsSave': 'Vitórias rápidas podem economizar',
      'recommendations.strategicFreeUp': 'Ações estratégicas podem liberar',
      'recommendations.immediately': 'imediatamente.',

      // Recommendation 1
      'rec1.title': 'Aproveitar Descontos de Pagamento Antecipado',
      'rec1.description':
        'Pagar fornecedores antecipadamente para capturar descontos de 2% em $280 mil em faturas',
      'rec1.impact': 'Economizar $5.600 imediatamente',
      'rec1.timeEstimate': '5 minutos',
      'rec1.action': 'Revisar e Pagar',

      // Recommendation 2
      'rec2.title': 'Atrasar Compra de Equipamento',
      'rec2.description':
        'Adiar compra de equipamento de $340 mil por 30 dias para melhorar o fluxo de caixa',
      'rec2.impact': 'Liberar $340 mil para capital de giro',
      'rec2.timeEstimate': '15 minutos',
      'rec2.action': 'Reagendar',

      // Recommendation 3
      'rec3.title': 'Configurar Lembretes de Pagamento',
      'rec3.description': 'Automatizar lembretes de pagamento para clientes com faturas vencidas',
      'rec3.impact': 'Reduzir DSO em 5-7 dias',
      'rec3.timeEstimate': '2 minutos',
      'rec3.action': 'Ativar',

      // Recommendation 4
      'rec4.title': 'Revisar Termos de Crédito',
      'rec4.description':
        'Negociar melhores termos de pagamento com fornecedores com base no histórico de pagamentos',
      'rec4.impact': 'Estender termos de pagamento em 15 dias',
      'rec4.timeEstimate': '10 minutos',
      'rec4.action': 'Contatar Fornecedores',

      // Payables
      'payables.controller': 'Controlador',
      'payables.financeOps': 'Operações Financeiras',
      'payables.learningJourney': 'Jornada de Aprendizado',
      'payables.createPayment': 'Criar Pagamento',
      'payables.totalBills': 'Total de Contas',
      'payables.pending': 'Pendente',
      'payables.overdue': 'Vencido',
      'payables.potentialSavings': 'Economias Potenciais',
      'payables.earlyPaymentDiscounts': 'Descontos por pagamento antecipado',
      'payables.payablesFinancing': 'Financiamento de Contas a Pagar',
      'payables.optimizeWorkingCapital':
        'Otimize seu capital de giro com soluções de pagamento flexíveis',
      'payables.newFeature': 'Novo Recurso',
      'payables.extendedPaymentTerms': 'Prazos de Pagamento Estendidos',
      'payables.availableCredit': 'Crédito Disponível',
      'payables.applyForFinancing': 'Solicitar Financiamento',
      'payables.viewOptions': 'Ver Opções',
      'payables.billsOverdue': 'contas vencidas',
      'payables.billOverdue': 'conta vencida',
      'payables.totalOverdueAmount': 'Valor total vencido',
      'payables.review': 'Revisar',
      'payables.billsAndPayments': 'Contas e Pagamentos',
      'payables.managePayables':
        'Gerencie suas contas a pagar com insights inteligentes e automação',
      'payables.table.billNumber': 'Número da Conta',
      'payables.table.supplier': 'Fornecedor',
      'payables.table.amount': 'Valor',
      'payables.table.dueDate': 'Data de Vencimento',
      'payables.table.status': 'Status',
      'payables.table.predictedPay': 'Pagamento Previsto',
      'payables.table.savings': 'Economias',
      'payables.table.terms': 'Condições',
      'payables.table.automation': 'Automação',
      'payables.table.actions': 'Ações',
      'payables.table.search': 'Pesquisar contas...',
      'payables.table.noBills': 'Nenhuma conta encontrada correspondente aos seus critérios.',
      'payables.table.daysOverdue': 'Dias Vencidos',
      'payables.status.pending': 'Pendente',
      'payables.status.overdue': 'Vencido',
      'payables.status.paid': 'Pago',
      'payables.status.scheduled': 'Programado',
      'payables.status.draft': 'Rascunho',
      'payables.status.approved': 'Aprovado',
      'payables.noBills': 'Nenhuma conta encontrada',
      'payables.noBillsDescription':
        'As contas aparecerão aqui quando forem criadas ou importadas do seu sistema contábil.',
      'payables.createFirstBill': 'Criar uma Conta',
      'payables.managePayablesWithInsights':
        'Gerencie suas contas a pagar com insights inteligentes e automação',
      'payables.paymentTermsDays': '30-90 dias',
      'payables.discountRange': '2-5%',
      'payables.availableCreditAmount': 'R$50K+',
      'payables.earlyPaymentDiscountsLabel': 'Descontos por Pagamento Antecipado',
      'payables.availableCreditLabel': 'Crédito Disponível',
      'payables.dpo': 'DPO (Dias a Pagar)',

      // Payments In
      'paymentsIn.overview': 'Visão Geral',
      'paymentsIn.recentPayments': 'Pagamentos Recentes',
      'paymentsIn.virtualAccounts': 'Contas Virtuais',
      'paymentsIn.reconciliation': 'Reconciliação',
      'paymentsIn.totalReceived': 'Total Recebido',
      'paymentsIn.pendingPayments': 'Pagamentos Pendentes',
      'paymentsIn.activeCustomers': 'Clientes Ativos',
      'paymentsIn.multiCurrency': 'Multi-Moeda',
      'paymentsIn.activeVirtualAccounts': 'Contas virtuais ativas',
      'paymentsIn.virtualAccountsSummary': 'Resumo de Contas Virtuais',
      'paymentsIn.usdAccount': 'Conta USD',
      'paymentsIn.eurAccount': 'Conta EUR',
      'paymentsIn.gbpAccount': 'Conta GBP',
      'paymentsIn.paymentReconciliation': 'Reconciliação de Pagamentos',
      'paymentsIn.unreconciledPayments': 'Pagamentos Não Reconciliados',
      'paymentsIn.paymentsNeedMatching': 'pagamentos precisam de conciliação',
      'paymentsIn.reconcile': 'Reconciliar',
      'paymentsIn.fromLastMonth': 'do mês passado',
      'paymentsIn.paymentsPending': 'pagamentos pendentes',
      'paymentsIn.received': 'Recebido',
      'paymentsIn.ago': 'atrás',
      'paymentsIn.hoursAgo': 'horas',
      'paymentsIn.dayAgo': 'dia',
      'paymentsIn.daysAgo': 'dias',

      // Component Titles
      'component.keyMetrics': 'Métricas Principais',
      'component.dataSources': 'Fontes de Dados',
      'component.prioritySupport': 'Suporte Prioritário',
      'component.cashPosition': 'Posição de Caixa',
      'component.waterfallCashFlow': 'Fluxo de Caixa em Cascata',
      'component.laborCosts': 'Custos de Mão de Obra (Line Type)',
      'component.budgetVsActuals': 'Orçamento vs Real',
      'component.scenarioPlanning': 'Planejamento de Cenários',
      'component.availableCredit': 'Crédito Disponível',
      'component.whatIfSimulation': 'Simulação E Se',
      'component.alertsNotifications': 'Alertas e Notificações',
      'component.smartRecommendations': 'Recomendações Inteligentes',
      'component.keyInsights': 'Insights Principais',
      'component.approvalManagement': 'Gestão de Aprovações',
      'component.approvalSummary': 'Resumo de Aprovações',
      'component.billsOverview': 'Visão Geral de Contas',
      'component.latePaymentTracker': 'Rastreador de Pagamentos Atrasados',
      'component.roiCalculator': 'Calculadora de ROI',
      'component.paymentAnalysisGrid': 'Grade de Análise de Pagamentos',
      'component.crossBorderAnalysis': 'Análise Transfronteiriça',
      'component.complianceMonitoring': 'Monitoramento de Conformidade',
      'component.cashForecastPlanning': 'Planejamento de Previsão de Caixa',
      'component.vendorPaymentOptimization': 'Otimização de Pagamentos a Fornecedores',
    },
  };

  return translations;
}

/**
 * Get translations for a specific language without React context.
 * Useful for non-React code like chart formatters and utilities.
 * Backed by the module-scope cache (see buildTranslations above).
 */
export function getTranslationsForLanguage(language: string) {
  if (!__translationsCache) __translationsCache = buildTranslations();
  return __translationsCache[language] || __translationsCache.en;
}

/**
 * Direct translation function against the GENERAL dictionary only, for
 * non-React contexts. NOTE: this does NOT walk core/portal/demo — for the
 * full resolution chain use resolveTranslation() from i18n/resolve.ts.
 */
export function translateGeneral(key: string, language: string = 'en', fallback?: string): string {
  const translations = getTranslationsForLanguage(language);
  return translations[key] || fallback || key;
}
